import React from 'react'
import { useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { 
  PlayIcon, 
  ExclamationTriangleIcon, 
  CheckCircleIcon, 
  ClockIcon,
  DocumentTextIcon,
  EyeIcon,
  ChartBarIcon,
  XMarkIcon,
  UserPlusIcon,
  ShieldExclamationIcon,
  CalendarIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  ArrowUpTrayIcon,
  CpuChipIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline'
import { readGamingDataSearchParams, toApiDateTimeParam } from './alertContextUtils'
import { formatGamingMoney, formatMeterCents, formatCashInWithBills, cashInCalculatedHint, formatCombinedOverlapPct } from '../../utils/gamingCurrency'
import { formatSydneyDateTime, formatSydneyDate } from '../../utils/sydneyDateTime'
import DailyTurnoverCharts from '../daily-data/DailyTurnoverCharts'
import TurnoverFocusSessions from '../daily-data/TurnoverFocusSessions'
import { fetchMeterReconciliation, reconChartFilters } from '../daily-data/meterReconciliationApi'

const METER_MONEY_FIELDS = new Set([
  'Turnover', 'CreditWin', 'CancelCredit', 'Credit',
  'CashlessIn', 'CashlessOut', 'CashIn', 'CashDrop', 'CoinOut',
])
const SESSION_MONEY_FIELDS = new Set([
  'Turnover', 'Stroke', 'CreditWin', 'ExternalWin', 'Rewards', 'PointsToCr',
])
const ALLOC_MONEY_FIELDS = new Set([
  'Turnover_alloc', 'turnover_alloc', 'CreditWin_alloc', 'creditwin_alloc',
  'CancelCredit_alloc', 'cancelcredit_alloc', 'CashlessOut_alloc', 'cashlessout_alloc',
  'CoinOut_alloc', 'coinout_alloc',
  'CashIn_alloc', 'cashin_alloc', 'CashlessIn_alloc', 'cashlessin_alloc',
  'TicketsIn_alloc', 'ticketsin_alloc', 'Turnover_converted', 'Turnover_credits',
])

function formatMeterField(key, value) {
  if (value == null || value === '') return '—'
  return METER_MONEY_FIELDS.has(key) ? formatMeterCents(value) : formatGamingMoney(value)
}

function formatSessionField(key, value) {
  if (value == null || value === '') return '—'
  return SESSION_MONEY_FIELDS.has(key) ? formatGamingMoney(value) : String(value)
}

function formatAllocField(key, value) {
  if (value == null || value === '') return '—'
  if (ALLOC_MONEY_FIELDS.has(key)) {
    if (key === 'Turnover_credits') return formatGamingMoney(value)
    return formatMeterCents(value)
  }
  return String(value)
}

/** Human-readable duration for combine/import timing (e.g. 45s, 2m 14s, 1h 5m). */
function formatElapsedMs(ms) {
  const n = Number(ms)
  if (!Number.isFinite(n) || n < 0) return null
  if (n < 1000) return `${Math.round(n)}ms`
  const totalSec = Math.round(n / 1000)
  if (totalSec < 60) return `${totalSec}s`
  const mins = Math.floor(totalSec / 60)
  const secs = totalSec % 60
  if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`
  const hours = Math.floor(mins / 60)
  const remMins = mins % 60
  return remMins > 0 ? `${hours}h ${remMins}m` : `${hours}h`
}

const COMBINE_UI_PHASES = ['clear', 'enrich', 'allocate', 'rollup', 'done']

/** Shown on Gaming Data tabs — source ingest vs Combined enrichment. */
const GAMING_SOURCE_TABS_NOTE =
  'Meter Reading, Sessions, EGM Registry, and Slot Analysis show ingested source files only.'
const GAMING_COMBINED_NOTE =
  'Combined is the only tab with enriched, attributed data — built from metering, carded sessions, slot analysis, and EGM registry via Process & Combine.'

function combineUiStepFromPoll(d) {
  const stats = d?.stats || {}
  const phase = String(d?.phase || stats.phase || 'queued').toLowerCase()
  if (Number.isFinite(d?.phase_index)) {
    return Math.min(COMBINE_UI_PHASES.length, Math.max(1, d.phase_index + 1))
  }
  if (phase === 'done') return COMBINE_UI_PHASES.length
  if (phase === 'queued') return 1
  const idx = COMBINE_UI_PHASES.indexOf(phase)
  return idx >= 0 ? idx + 1 : 1
}

function combinePctFromPoll(d, uiStep) {
  if (d?.pct != null && Number.isFinite(d.pct)) {
    return Math.max(5, Math.min(99, d.pct))
  }
  return Math.max(5, Math.min(99, Math.round((uiStep / COMBINE_UI_PHASES.length) * 100)))
}

/** Read a numeric alloc field with case-insensitive fallback (PG drivers vary). */
function allocNum(row, ...keys) {
  if (!row) return 0
  for (const k of keys) {
    if (row[k] != null && row[k] !== '') {
      const n = Number(row[k])
      if (Number.isFinite(n)) return n
    }
  }
  const lower = Object.create(null)
  for (const [k, v] of Object.entries(row)) {
    if (typeof k === 'string') lower[k.toLowerCase()] = v
  }
  for (const k of keys) {
    const v = lower[String(k).toLowerCase()]
    if (v != null && v !== '') {
      const n = Number(v)
      if (Number.isFinite(n)) return n
    }
  }
  return 0
}

/** Attendant handpay + ticket/coin out (meter cents). Matches IGT “Payouts” components in Combined. */
function allocPayoutCents(row) {
  return allocNum(row, 'CancelCredit_alloc', 'cancelcredit_alloc')
    + allocNum(row, 'CashlessOut_alloc', 'cashlessout_alloc')
    + allocNum(row, 'CoinOut_alloc', 'coinout_alloc')
}

function formatPayoutAlloc(row) {
  const cents = allocPayoutCents(row)
  return cents > 0 ? formatMeterCents(cents) : '—'
}

const EGM_FILTER_PLACEHOLDER = 'House 010246, 246, or machine pk'

/** Floor house number (what staff search for). */
function combinedEgmHouse(r) {
  return r?.HouseNumber ?? r?.house_number ?? r?.EgmPK ?? r?.egmpk ?? '—'
}

/** DimMachine / vendor machine_pk (meter file col 0) when different from house. */
function combinedEgmMachineId(r) {
  const house = String(combinedEgmHouse(r))
  const vendor = r?.VendorMachinePk ?? r?.vendor_machine_pk
  const egmId = r?.EgmId ?? r?.egm_id
  if (vendor && String(vendor) !== house) return vendor
  if (egmId && String(egmId) !== house) return egmId
  return vendor ?? egmId ?? '—'
}

function combinedRowSortTime(row, view) {
  if (view === 'sessions') {
    const raw = row?.SessionStart ?? row?.FirstInterval ?? row?.sessionstart ?? row?.firstinterval
    if (!raw) return 0
    const d = new Date(typeof raw === 'string' && !raw.includes('T') ? raw.replace(' ', 'T') : raw)
    return Number.isFinite(d.getTime()) ? d.getTime() : 0
  }
  const raw = combinedIntervalStart(row)
  if (!raw) return 0
  const d = new Date(typeof raw === 'string' && !raw.includes('T') ? raw.replace(' ', 'T') : raw)
  return Number.isFinite(d.getTime()) ? d.getTime() : 0
}

function sortCombinedRowsChronologically(rows, view) {
  if (!Array.isArray(rows) || rows.length < 2) return rows || []
  return [...rows].sort((a, b) => combinedRowSortTime(a, view) - combinedRowSortTime(b, view))
}

function memberDayCacheKey(memberId, tradingDay) {
  const day = String(tradingDay || '').slice(0, 10)
  return `${String(memberId ?? '')}|${day}`
}

function memberDayTradingDayParam(row, fallbackDay = '') {
  const raw = row?.TradingDay ?? row?.tradingday ?? row?.trading_day ?? fallbackDay
  if (!raw) return String(fallbackDay || '').slice(0, 10)
  if (raw instanceof Date) return raw.toISOString().slice(0, 10)
  return String(raw).slice(0, 10)
}

function annotateMemberDaySessionCumulative(sessions) {
  const sorted = sortCombinedRowsChronologically(sessions, 'sessions')
  let cumTo = 0
  let cumWin = 0
  return sorted.map((s) => {
    const to = allocNum(s, 'Turnover_alloc', 'turnover_alloc')
    const win = allocNum(s, 'CreditWin_alloc', 'creditwin_alloc')
    cumTo += to
    cumWin += win
    return {
      ...s,
      MemberDayCumTurnover: cumTo,
      MemberDayCumCreditWin: cumWin,
      MemberDayCumNet: cumTo - cumWin,
    }
  })
}

function CombinedSessionIntervalTable({ session, sessionPk, parseDate }) {
  const ivs = Array.isArray(session?.intervals) ? session.intervals : []
  if (!ivs.length) {
    return <p className="text-sm text-gray-500">No interval detail returned for this session.</p>
  }
  return (
    <div className="overflow-x-auto rounded border border-slate-200 bg-white">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th className="px-2 py-1.5 text-left">Interval</th>
            <th className="px-2 py-1.5 text-right">Turnover</th>
            <th className="px-2 py-1.5 text-right">CreditWin</th>
            <th className="px-2 py-1.5 text-right" title="Cancel credit (attendant handpay)">CancelCr</th>
            <th className="px-2 py-1.5 text-right" title="Cashless / ticket out">CashlessOut</th>
            <th className="px-2 py-1.5 text-right" title="Meter stroke delta (games/handle count)">Stroke</th>
            <th className="px-2 py-1.5 text-right">CashIn</th>
            <th className="px-2 py-1.5 text-right">Cashless</th>
            <th className="px-2 py-1.5 text-right">Tickets</th>
            <th className="px-2 py-1.5 text-right">Cum TO</th>
            <th className="px-2 py-1.5 text-right">Cum Win</th>
            <th className="px-2 py-1.5 text-right">Cum Payout</th>
            <th className="px-2 py-1.5 text-right">Cum Stroke</th>
            <th className="px-2 py-1.5 text-right">Cum Net</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {ivs.map((iv, i) => {
            const d = parseDate(iv.IntervalStart ?? iv.intervalstart)
            const net = Number(iv.Net)
            const ivStroke = allocNum(iv, 'Stroke_alloc', 'stroke_alloc')
            const cumStroke = Number(iv.CumStroke ?? iv.cumstroke ?? 0) || 0
            const cumPayout = Number(iv.CumPayout ?? iv.cumpayout ?? 0) || 0
            return (
              <tr key={`${sessionPk}-${i}`}>
                <td className="px-2 py-1.5 whitespace-nowrap">{d ? formatSydneyDateTime(d) : '—'}</td>
                <td className="px-2 py-1.5 text-right">{formatAllocField('Turnover_alloc', iv.Turnover_alloc)}</td>
                <td className="px-2 py-1.5 text-right">{formatAllocField('CreditWin_alloc', iv.CreditWin_alloc)}</td>
                <td className={`px-2 py-1.5 text-right ${allocNum(iv, 'CancelCredit_alloc', 'cancelcredit_alloc') > 0 ? 'text-rose-700 font-medium' : ''}`}>
                  {formatAllocField('CancelCredit_alloc', iv.CancelCredit_alloc)}
                </td>
                <td className={`px-2 py-1.5 text-right ${allocNum(iv, 'CashlessOut_alloc', 'cashlessout_alloc') > 0 ? 'text-rose-700 font-medium' : ''}`}>
                  {formatAllocField('CashlessOut_alloc', iv.CashlessOut_alloc)}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">{ivStroke > 0 ? Math.round(ivStroke).toLocaleString() : '—'}</td>
                <td
                  className="px-2 py-1.5 text-right"
                  title={cashInCalculatedHint(iv, (Number(iv.CashIn_alloc) || 0) + (Number(iv.CashDrop_alloc) || 0)) || undefined}
                >
                  {formatCashInWithBills(iv, (Number(iv.CashIn_alloc) || 0) + (Number(iv.CashDrop_alloc) || 0))}
                </td>
                <td className="px-2 py-1.5 text-right">{formatAllocField('CashlessIn_alloc', iv.CashlessIn_alloc)}</td>
                <td className="px-2 py-1.5 text-right">{formatAllocField('TicketsIn_alloc', iv.TicketsIn_alloc)}</td>
                <td className="px-2 py-1.5 text-right">{formatAllocField('Turnover_alloc', iv.CumTurnover)}</td>
                <td className="px-2 py-1.5 text-right">{formatAllocField('CreditWin_alloc', iv.CumCreditWin)}</td>
                <td className={`px-2 py-1.5 text-right ${cumPayout > 0 ? 'text-rose-700 font-medium' : ''}`}>
                  {cumPayout > 0 ? formatMeterCents(cumPayout) : '—'}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">{cumStroke > 0 ? Math.round(cumStroke).toLocaleString() : '—'}</td>
                <td className={`px-2 py-1.5 text-right font-medium ${net >= 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                  {formatAllocField('Turnover_alloc', net)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/**
 * By-session filters (AUD dollars in UI; alloc fields are meter cents).
 * Applied on the client at display time so stale/racy API responses cannot show wrong rows.
 */
function applyCombinedSessionFilters(rows, f) {
  if (!Array.isArray(rows) || !f) return rows || []
  let next = rows
  if (f.has_cash_in) {
    next = next.filter((r) => allocNum(r, 'CashIn_alloc', 'cashin_alloc') + allocNum(r, 'CashDrop_alloc', 'cashdrop_alloc') > 0)
  }
  const minTo = Number(f.min_turnover)
  if (Number.isFinite(minTo) && minTo > 0) {
    const minCents = minTo * 100
    next = next.filter((r) => allocNum(r, 'Turnover_alloc', 'turnover_alloc') >= minCents)
  }
  if (f.net_result === 'loss') {
    next = next.filter(
      (r) => allocNum(r, 'Turnover_alloc', 'turnover_alloc') - allocNum(r, 'CreditWin_alloc', 'creditwin_alloc') > 0
    )
  } else if (f.net_result === 'gain') {
    next = next.filter(
      (r) => allocNum(r, 'Turnover_alloc', 'turnover_alloc') - allocNum(r, 'CreditWin_alloc', 'creditwin_alloc') < 0
    )
  }
  const minGain = Number(f.min_net_gain)
  if (Number.isFinite(minGain) && minGain > 0) {
    const minGainCents = minGain * 100
    next = next.filter(
      (r) =>
        allocNum(r, 'CreditWin_alloc', 'creditwin_alloc') - allocNum(r, 'Turnover_alloc', 'turnover_alloc') >= minGainCents
    )
  }
  if (f.has_jackpot) {
    next = next.filter((r) => {
      const sessJp = Number(r.EnrichedSessionJackpot ?? r.enrichedsessionjackpot ?? 0) || 0
      const dayJp = Number(r.EnrichedDailyJackpot ?? r.enricheddailyjackpot ?? 0) || 0
      const dayProg = Number(r.EnrichedDailyProgressive ?? r.enricheddailyprogressive ?? 0) || 0
      return enrichFlagYes(r.EnrichedHasJackpot ?? r.enrichedhasjackpot) || sessJp > 0 || dayJp > 0 || dayProg > 0
    })
  }
  return next
}

const GAMING_MEMBER_FILTER_LABEL = 'Member # or Player ID'
const GAMING_MEMBER_FILTER_PLACEHOLDER = 'Club membership # or IGT player id'

function toDatetimeLocalValue(d) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function defaultMeterFromDate() {
  const d = new Date()
  return toDatetimeLocalValue(new Date(d.getFullYear(), d.getMonth(), 1, 0, 0))
}

function defaultMeterToDate() {
  return toDatetimeLocalValue(new Date())
}

function useVenueOptions() {
  const [venues, setVenues] = React.useState([])
  React.useEffect(() => {
    authFetch(getLocalApiUrl('/api/venues'))
      .then((r) => r.json())
      .then((j) => {
        const list = (j.data || j.venues || []).map((v) => ({
          venue_id: v.venue_id || v.id,
          name: v.name,
          gaming_extract_venue_name: v.gaming_extract_venue_name || null,
        }))
        setVenues(list.filter((v) => v.venue_id))
      })
      .catch(() => setVenues([]))
  }, [])
  return venues
}

function MeterFilters({ filters, setFilters, onReload, venues, hideSample, setHideSample }) {
  return (
    <div className="card mb-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Venue</label>
          <select
            className="input-field w-full"
            value={filters.venue_id || ''}
            onChange={(e) => setFilters((prev) => ({ ...prev, venue_id: e.target.value }))}
          >
            <option value="">All venues</option>
            {venues.map((v) => (
              <option key={v.venue_id} value={v.venue_id}>
                {v.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">EGM ID</label>
          <input className="input-field w-full" value={filters.egm_id} onChange={(e)=>setFilters(prev=>({ ...prev, egm_id:e.target.value }))} />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">From</label>
          <input type="datetime-local" className="input-field w-full" value={filters.from} onChange={(e)=>setFilters(prev=>({ ...prev, from:e.target.value }))} />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">To</label>
          <input type="datetime-local" className="input-field w-full" value={filters.to} onChange={(e)=>setFilters(prev=>({ ...prev, to:e.target.value }))} />
        </div>
        <div className="flex items-end">
          <button type="button" className="btn-primary w-full sm:w-auto" onClick={onReload}>Reload</button>
        </div>
        <div className="flex items-end">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer pb-2">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={hideSample}
              onChange={(e) => setHideSample(e.target.checked)}
            />
            Hide sample EGMs
          </label>
        </div>
      </div>
    </div>
  )
}

function Filters({ filters, setFilters, onReload }) {
  return (
    <div className="card mb-4">
      <div className="grid grid-cols-6 gap-3">
        <div>
          <label className="block text-xs text-gray-600 mb-1">EGM ID</label>
          <input className="input-field w-full" value={filters.egm_id} onChange={(e)=>setFilters(prev=>({ ...prev, egm_id:e.target.value }))} />
        </div>
        {/* Venue not part of vendor meter sheet; removed */}
        <div>
          <label className="block text-xs text-gray-600 mb-1">From</label>
          <input type="datetime-local" className="input-field w-full" value={filters.from} onChange={(e)=>setFilters(prev=>({ ...prev, from:e.target.value }))} />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">To</label>
          <input type="datetime-local" className="input-field w-full" value={filters.to} onChange={(e)=>setFilters(prev=>({ ...prev, to:e.target.value }))} />
        </div>
        <div className="flex items-end">
          <button className="btn-primary" onClick={onReload}>Reload</button>
        </div>
      </div>
    </div>
  )
}

function MeterTab({ initialFilters }) {
  const METER_LIMIT = 100
  const venues = useVenueOptions()
  const [items, setItems] = React.useState([])
  const [total, setTotal] = React.useState(0)
  const [page, setPage] = React.useState(1)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [hideSample, setHideSample] = React.useState(true)
  const [filters, setFilters] = React.useState(() => ({
    egm_id: initialFilters?.egm_pk || initialFilters?.egm_id || '',
    venue_id: initialFilters?.venue_id || '',
    from: initialFilters?.from || defaultMeterFromDate(),
    to: initialFilters?.to || defaultMeterToDate(),
  }))
  const [uploading, setUploading] = React.useState(false)
  const [uploadProgress, setUploadProgress] = React.useState(0)
  const [defaultTemplateId, setDefaultTemplateId] = React.useState(null)
  React.useEffect(() => {
    authFetch(getLocalApiUrl('/api/configuration')).then(r => r.json()).then(j => {
      const imp = (j.data || []).find(c => c.category === 'Import')
      const by = Object.fromEntries((imp?.settings || []).map(s => [s.name, s]))
      setDefaultTemplateId(by['IMPORT_MAPPING_DEFAULT_METER']?.value || null)
    }).catch(() => {})
  }, [])

  const load = React.useCallback(async (pg = 1)=>{
    try {
      setLoading(true); setError(null)
      const params = new URLSearchParams()
      if (filters.egm_id) params.set('egm_pk', filters.egm_id)
      if (filters.venue_id) params.set('venue_id', filters.venue_id)
      if (filters.from) params.set('from', toApiDateTimeParam(filters.from))
      if (filters.to) params.set('to', toApiDateTimeParam(filters.to))
      if (hideSample) params.set('exclude_sample', 'true')
      params.set('limit', String(METER_LIMIT))
      params.set('page', String(pg))
      const r = await authFetch(getLocalApiUrl(`/api/gaming/egms/readings?${params}`))
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed to load meter readings')
      setItems(j.data || [])
      setTotal(j.total || j.data?.length || 0)
      setPage(pg)
    } catch (e) { setError(e.message) } finally { setLoading(false) }
  }, [filters, hideSample])

  React.useEffect(()=>{ load(1) }, [load])

  const handleUpload = React.useCallback(async(file)=>{
    if (!file) return
    setUploading(true); setUploadProgress(0)
    try {
      await new Promise((resolve, reject)=>{
        const fd = new FormData(); fd.append('file', file)
        if (defaultTemplateId && /\.(txt|csv)$/i.test(file.name)) fd.append('template_id', defaultTemplateId)
        const xhr = new XMLHttpRequest()
        xhr.open('POST', getLocalApiUrl('/api/gaming/egms/readings/upload'))
        xhr.upload.onprogress = (e)=>{ if (e.lengthComputable) setUploadProgress(Math.round((e.loaded/e.total)*100)) }
        xhr.onload = ()=>{
          try { const j = JSON.parse(xhr.responseText || '{}'); if (j.success !== false) resolve(j); else reject(new Error(j.error||'Upload failed')) } catch(_) { resolve({}) }
        }
        xhr.onerror = ()=> reject(new Error('Network error'))
        xhr.send(fd)
      })
      await load(1)
    } catch (e) { setError(e.message) }
    finally { setUploading(false); setUploadProgress(0) }
  }, [load, defaultTemplateId])

  return (
    <div>
      <MeterFilters
        filters={filters}
        setFilters={setFilters}
        onReload={() => load(1)}
        venues={venues}
        hideSample={hideSample}
        setHideSample={setHideSample}
      />
      <p className="text-xs text-gray-500 mb-2 max-w-4xl">
        Ingested meter readings only (<code className="text-xs">egm_meter_readings</code> from RXConnect / Azure import).
        This is source data — not Combined allocation or virtual uncarded sits. Use <strong>Combined</strong> for attributed intervals.
        Sample EGMs are hidden by default.
        {total > 0 && <span className="ml-1">Showing {items.length} of {total} rows.</span>}
      </p>
      <div className="card">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-medium text-gray-900">Ingested Meter Readings</h3>
          <div className="flex items-center gap-2">
            <form onSubmit={(e)=> e.preventDefault()}>
              <input type="file" name="file" accept=".xlsx,.xls,.txt,.csv" className="hidden" id="upload-meter" onChange={(e)=>{ const f=e.target.files?.[0]; if (f) handleUpload(f); e.target.value='' }} />
              <label htmlFor="upload-meter" className="btn-secondary cursor-pointer">Upload (.xlsx, .txt, .csv)</label>
            </form>
            <div className="text-sm text-gray-500">{uploading ? `Uploading ${uploadProgress}%` : (loading ? 'Loading…' : '')}</div>
          </div>
        </div>
        {error && <div className="text-sm text-red-600 mb-2">{error}</div>}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Snapshot Time</th>
                <th className="px-3 py-2 text-left">EgmPK</th>
                <th className="px-3 py-2 text-left">Venue</th>
                <th className="px-3 py-2 text-left">GamingDate</th>
                <th className="px-3 py-2 text-left">MeterReadTime</th>
                <th className="px-3 py-2 text-left">Turnover</th>
                <th className="px-3 py-2 text-left">CreditWin</th>
                <th className="px-3 py-2 text-left">CancelCredit</th>
                <th className="px-3 py-2 text-left">Stroke</th>
                <th className="px-3 py-2 text-left">Credit</th>
                <th className="px-3 py-2 text-left">CashlessIn</th>
                <th className="px-3 py-2 text-left">CashlessOut</th>
                <th className="px-3 py-2 text-left">CashIn</th>
                <th className="px-3 py-2 text-left">CashDrop</th>
                <th className="px-3 py-2 text-left">CoinOut</th>
                <th className="px-3 py-2 text-left">Bill5</th>
                <th className="px-3 py-2 text-left">Bill10</th>
                <th className="px-3 py-2 text-left">Bill20</th>
                <th className="px-3 py-2 text-left">Bill50</th>
                <th className="px-3 py-2 text-left">Bill100</th>
                <th className="px-3 py-2 text-left">TicketsIn</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {items.map((r)=> (
                <tr key={`${r.EgmPK}-${r.SnapshotTime}`}>
                  <td className="px-3 py-2">{formatSydneyDateTime(r.SnapshotTime)}</td>
                  <td className="px-3 py-2 font-mono">{r.EgmPK}</td>
                  <td className="px-3 py-2 text-xs">{r.Venue || '—'}</td>
                  <td className="px-3 py-2">{r.GamingDate || '—'}</td>
                  <td className="px-3 py-2">{r.MeterReadTime || '—'}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatMeterField('Turnover', r.Turnover)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatMeterField('CreditWin', r.CreditWin)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatMeterField('CancelCredit', r.CancelCredit)}</td>
                  <td className="px-3 py-2 text-right">{r.Stroke ?? '—'}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatMeterField('Credit', r.Credit)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatMeterField('CashlessIn', r.CashlessIn)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatMeterField('CashlessOut', r.CashlessOut)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatMeterField('CashIn', r.CashIn)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatMeterField('CashDrop', r.CashDrop)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatMeterField('CoinOut', r.CoinOut)}</td>
                  <td className="px-3 py-2 text-right">{r.Bill5 ?? '—'}</td>
                  <td className="px-3 py-2 text-right">{r.Bill10 ?? '—'}</td>
                  <td className="px-3 py-2 text-right">{r.Bill20 ?? '—'}</td>
                  <td className="px-3 py-2 text-right">{r.Bill50 ?? '—'}</td>
                  <td className="px-3 py-2 text-right">{r.Bill100 ?? '—'}</td>
                  <td className="px-3 py-2 text-right">{r.TicketsIn ?? '—'}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td className="px-3 py-4 text-gray-500" colSpan={21}>No readings — try widening the date range, picking a venue, or unchecking Hide sample EGMs</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {total > METER_LIMIT && (
          <div className="flex items-center gap-2 text-sm mt-3">
            <button className="btn-secondary" disabled={page <= 1} onClick={() => load(page - 1)}>Previous</button>
            <span className="text-gray-500">Page {page} of {Math.ceil(total / METER_LIMIT)}</span>
            <button className="btn-secondary" disabled={page >= Math.ceil(total / METER_LIMIT)} onClick={() => load(page + 1)}>Next</button>
          </div>
        )}
      </div>
    </div>
  )
}

function SessionsTab({ initialFilters }) {
  const SESS_LIMIT = 100
  const venues = useVenueOptions()
  const [items, setItems] = React.useState([])
  const [total, setTotal] = React.useState(0)
  const [page, setPage] = React.useState(1)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [filters, setFilters] = React.useState(() => ({
    egm_pk: initialFilters?.egm_pk || '',
    venue_id: initialFilters?.venue_id || '',
    member_id: initialFilters?.member_id || '',
    from: initialFilters?.from || defaultMeterFromDate(),
    to: initialFilters?.to || defaultMeterToDate(),
  }))
  const [uploading, setUploading] = React.useState(false)
  const [uploadProgress, setUploadProgress] = React.useState(0)
  const [defaultTemplateId, setDefaultTemplateId] = React.useState(null)
  React.useEffect(() => {
    authFetch(getLocalApiUrl('/api/configuration')).then(r => r.json()).then(j => {
      const imp = (j.data || []).find(c => c.category === 'Import')
      const by = Object.fromEntries((imp?.settings || []).map(s => [s.name, s]))
      setDefaultTemplateId(by['IMPORT_MAPPING_DEFAULT_SESSIONS']?.value || null)
    }).catch(() => {})
  }, [])

  const load = React.useCallback(async (pg = 1)=>{
    try {
      setLoading(true); setError(null)
      const qs = new URLSearchParams(
        Object.fromEntries(
          Object.entries(filters)
            .filter(([, v]) => v)
            .map(([k, v]) => [k, k === 'from' || k === 'to' ? toApiDateTimeParam(v) : v])
        )
      )
      qs.set('limit', String(SESS_LIMIT))
      qs.set('page', String(pg))
      const r = await authFetch(getLocalApiUrl(`/api/gaming/sessions?${qs}`))
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed to load sessions')
      setItems(j.data || [])
      setTotal(j.total || j.data?.length || 0)
      setPage(pg)
    } catch (e) { setError(e.message) } finally { setLoading(false) }
  }, [filters])

  React.useEffect(()=>{ load(1) }, [load])

  const handleUpload = React.useCallback(async(file)=>{
    if (!file) return
    setUploading(true); setUploadProgress(0)
    try {
      await new Promise((resolve, reject)=>{
        const fd = new FormData(); fd.append('file', file)
        if (defaultTemplateId && /\.(txt|csv)$/i.test(file.name)) fd.append('template_id', defaultTemplateId)
        const xhr = new XMLHttpRequest()
        xhr.open('POST', getLocalApiUrl('/api/gaming/sessions/upload'))
        xhr.upload.onprogress = (e)=>{ if (e.lengthComputable) setUploadProgress(Math.round((e.loaded/e.total)*100)) }
        xhr.onload = ()=>{
          try { const j = JSON.parse(xhr.responseText || '{}'); if (j.success !== false) resolve(j); else reject(new Error(j.error||'Upload failed')) } catch(_) { resolve({}) }
        }
        xhr.onerror = ()=> reject(new Error('Network error'))
        xhr.send(fd)
      })
      await load(1)
    } catch (e) { setError(e.message) }
    finally { setUploading(false); setUploadProgress(0) }
  }, [load, defaultTemplateId])

  return (
    <div>
      <div className="card mb-4">
        <div className="grid grid-cols-6 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">House / EGM</label>
            <input className="input-field w-full" placeholder="010038 or 38" value={filters.egm_pk} onChange={(e)=>setFilters(prev=>({ ...prev, egm_pk:e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Venue</label>
            <select
              className="input-field w-full"
              value={filters.venue_id || ''}
              onChange={(e) => setFilters((prev) => ({ ...prev, venue_id: e.target.value }))}
            >
              <option value="">All venues</option>
              {venues.map((v) => (
                <option key={v.venue_id} value={v.venue_id}>{v.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">{GAMING_MEMBER_FILTER_LABEL}</label>
            <input className="input-field w-full" placeholder={GAMING_MEMBER_FILTER_PLACEHOLDER} value={filters.member_id} onChange={(e)=>setFilters(prev=>({ ...prev, member_id:e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">From</label>
            <input type="datetime-local" className="input-field w-full" value={filters.from} onChange={(e)=>setFilters(prev=>({ ...prev, from:e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">To</label>
            <input type="datetime-local" className="input-field w-full" value={filters.to} onChange={(e)=>setFilters(prev=>({ ...prev, to:e.target.value }))} />
          </div>
          <div className="flex items-end">
            <button className="btn-primary" onClick={() => load(1)}>Reload</button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Imported carded sessions only (<code className="text-xs">FactPlayerSession</code> → <code className="text-xs">gaming_sessions</code>). Virtual uncarded sits from Process &amp; Combine are on the <strong>Combined</strong> tab, not here.
          {total > 0 && <span className="ml-1">Showing {items.length} of {total} rows.</span>}
        </p>
      </div>
      <div className="card">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-medium text-gray-900">Imported Carded Sessions</h3>
          <div className="flex items-center gap-2">
            <form onSubmit={(e)=> e.preventDefault()}>
              <input type="file" name="file" accept=".xlsx,.xls,.txt,.csv" className="hidden" id="upload-sessions" onChange={(e)=>{ const f=e.target.files?.[0]; if (f) handleUpload(f); e.target.value='' }} />
              <label htmlFor="upload-sessions" className="btn-secondary cursor-pointer">Upload (.xlsx, .txt, .csv)</label>
            </form>
            <div className="text-sm text-gray-500">{uploading ? `Uploading ${uploadProgress}%` : (loading ? 'Loading…' : '')}</div>
          </div>
        </div>
        {error && <div className="text-sm text-red-600 mb-2">{error}</div>}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">SessionPK</th>
                <th className="px-3 py-2 text-left">Player ID</th>
                <th className="px-3 py-2 text-left">EgmName</th>
                <th className="px-3 py-2 text-left">EgmPK</th>
                <th className="px-3 py-2 text-left">GameVariationPK</th>
                <th className="px-3 py-2 text-left">GamingDate</th>
                <th className="px-3 py-2 text-left">StartTime</th>
                <th className="px-3 py-2 text-left">EndTime</th>
                <th className="px-3 py-2 text-left">Turnover</th>
                <th className="px-3 py-2 text-left">Stroke</th>
                <th className="px-3 py-2 text-left">CreditWin</th>
                <th className="px-3 py-2 text-left">ExternalWin</th>
                <th className="px-3 py-2 text-left">Rewards</th>
                <th className="px-3 py-2 text-left">PointsToCr</th>
                <th className="px-3 py-2 text-left">Venue</th>
                <th className="px-3 py-2 text-left">MeterValid</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {items.map((r)=> (
                <tr key={r.SessionPK}>
                  <td className="px-3 py-2 font-mono">{r.SessionPK}</td>
                  <td className="px-3 py-2">{r.MemberID || '—'}</td>
                  <td className="px-3 py-2">{r.EgmName || '—'}</td>
                  <td className="px-3 py-2 font-mono">{r.EgmPK}</td>
                  <td className="px-3 py-2">{r.GameVariationPK || '—'}</td>
                  <td className="px-3 py-2">{r.GamingDate || '—'}</td>
                  <td className="px-3 py-2">{r.StartTime ? formatSydneyDateTime(r.StartTime) : '—'}</td>
                  <td className="px-3 py-2">{r.EndTime ? formatSydneyDateTime(r.EndTime) : '—'}</td>
                  <td className="px-3 py-2 text-right">{formatSessionField('Turnover', r.Turnover)}</td>
                  <td className="px-3 py-2 text-right">{formatSessionField('Stroke', r.Stroke)}</td>
                  <td className="px-3 py-2 text-right">{formatSessionField('CreditWin', r.CreditWin)}</td>
                  <td className="px-3 py-2 text-right">{formatSessionField('ExternalWin', r.ExternalWin)}</td>
                  <td className="px-3 py-2 text-right">{formatSessionField('Rewards', r.Rewards)}</td>
                  <td className="px-3 py-2 text-right">{formatSessionField('PointsToCr', r.PointsToCr)}</td>
                  <td className="px-3 py-2">{r.Venue ?? '—'}</td>
                  <td className="px-3 py-2">{r.MeterValid ?? '—'}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td className="px-3 py-4 text-gray-500" colSpan={16}>No sessions</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {total > SESS_LIMIT && (
          <div className="flex items-center gap-2 text-sm mt-3">
            <button className="btn-secondary" disabled={page <= 1} onClick={() => load(page - 1)}>Previous</button>
            <span className="text-gray-500">Page {page} of {Math.ceil(total / SESS_LIMIT)}</span>
            <button className="btn-secondary" disabled={page >= Math.ceil(total / SESS_LIMIT)} onClick={() => load(page + 1)}>Next</button>
          </div>
        )}
      </div>
    </div>
  )
}

function toDatetimeLocal(d) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function ImportWizardTab() {
  const defaultFrom = React.useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - 7); d.setHours(0, 0, 0, 0); return toDatetimeLocal(d)
  }, [])
  const defaultTo = React.useMemo(() => {
    const d = new Date(); d.setHours(23, 59, 0, 0); return toDatetimeLocal(d)
  }, [])
  const [dateRange, setDateRange] = React.useState({ from: defaultFrom, to: defaultTo })
  const [meterTemplates, setMeterTemplates] = React.useState([])
  const [sessionsTemplates, setSessionsTemplates] = React.useState([])
  const [patronTemplates, setPatronTemplates] = React.useState([])
  const [egmTemplates, setEgmTemplates] = React.useState([])
  const [meterTemplateId, setMeterTemplateId] = React.useState('')
  const [sessionsTemplateId, setSessionsTemplateId] = React.useState('')
  const [patronTemplateId, setPatronTemplateId] = React.useState('')
  const [egmTemplateId, setEgmTemplateId] = React.useState('')
  const [loading, setLoading] = React.useState(true)
  const [meterUploading, setMeterUploading] = React.useState(false)
  const [sessionsUploading, setSessionsUploading] = React.useState(false)
  const [patronUploading, setPatronUploading] = React.useState(false)
  const [egmUploading, setEgmUploading] = React.useState(false)
  const [meterProgress, setMeterProgress] = React.useState(0)
  const [sessionsProgress, setSessionsProgress] = React.useState(0)
  const [patronProgress, setPatronProgress] = React.useState(0)
  const [egmProgress, setEgmProgress] = React.useState(0)
  const [meterResult, setMeterResult] = React.useState(null)
  const [sessionsResult, setSessionsResult] = React.useState(null)
  const [patronResult, setPatronResult] = React.useState(null)
  const [egmResult, setEgmResult] = React.useState(null)
  const [meterError, setMeterError] = React.useState(null)
  const [sessionsError, setSessionsError] = React.useState(null)
  const [patronError, setPatronError] = React.useState(null)
  const [egmError, setEgmError] = React.useState(null)

  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const [cfgRes, tmplRes] = await Promise.all([
          authFetch(getLocalApiUrl('/api/configuration')),
          authFetch(getLocalApiUrl('/api/import-mapping-templates'))
        ])
        const cfg = await cfgRes.json()
        const tmpl = await tmplRes.json()
        if (cancelled || !cfg.success || !tmpl.success) return
        const templates = tmpl.data || []
        const meterList = templates.filter(t => t.mapping_type === 'meter')
        const sessionsList = templates.filter(t => t.mapping_type === 'sessions')
        const patronList = templates.filter(t => t.mapping_type === 'patron')
        const egmList = templates.filter(t => t.mapping_type === 'egm')
        setMeterTemplates(meterList)
        setSessionsTemplates(sessionsList)
        setPatronTemplates(patronList)
        setEgmTemplates(egmList)
        const importCat = (cfg.data || []).find(c => c.category === 'Import')
        const byName = Object.fromEntries((importCat?.settings || []).map(s => [s.name, s]))
        const defaultMeterId = (byName['IMPORT_MAPPING_DEFAULT_METER']?.value || '').trim()
        const defaultSessionsId = (byName['IMPORT_MAPPING_DEFAULT_SESSIONS']?.value || '').trim()
        const defaultPatronId = (byName['IMPORT_MAPPING_DEFAULT_PATRON']?.value || '').trim()
        const defaultEgmId = (byName['IMPORT_MAPPING_DEFAULT_EGM']?.value || '').trim()
        if (defaultMeterId && meterList.some(t => String(t.id) === defaultMeterId)) setMeterTemplateId(defaultMeterId)
        else if (meterList.length) setMeterTemplateId(String(meterList[0].id))
        if (defaultSessionsId && sessionsList.some(t => String(t.id) === defaultSessionsId)) setSessionsTemplateId(defaultSessionsId)
        else if (sessionsList.length) setSessionsTemplateId(String(sessionsList[0].id))
        if (defaultPatronId && patronList.some(t => String(t.id) === defaultPatronId)) setPatronTemplateId(defaultPatronId)
        else if (patronList.length) setPatronTemplateId(String(patronList[0].id))
        if (defaultEgmId && egmList.some(t => String(t.id) === defaultEgmId)) setEgmTemplateId(defaultEgmId)
        else if (egmList.length) setEgmTemplateId(String(egmList[0].id))
      } catch (e) { if (!cancelled) console.error('Import wizard load failed', e) }
      finally { if (!cancelled) setLoading(false) }
    })()
    return () => { cancelled = true }
  }, [])

  const meterTemplate = meterTemplates.find(t => String(t.id) === meterTemplateId)
  const sessionsTemplate = sessionsTemplates.find(t => String(t.id) === sessionsTemplateId)
  const patronTemplate = patronTemplates.find(t => String(t.id) === patronTemplateId)
  const egmTemplate = egmTemplates.find(t => String(t.id) === egmTemplateId)

  const uploadMeter = React.useCallback(async (file) => {
    if (!file) return
    setMeterUploading(true); setMeterProgress(0); setMeterError(null); setMeterResult(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      if (meterTemplateId) fd.append('template_id', String(meterTemplateId))
      const xhr = new XMLHttpRequest()
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) setMeterProgress(Math.round((e.loaded / e.total) * 100)) }
      const result = await new Promise((resolve, reject) => {
        xhr.onload = () => {
          try {
            const j = JSON.parse(xhr.responseText || '{}')
            if (j.success !== false) resolve(j)
            else reject(new Error(j.error || 'Upload failed'))
          } catch (_) { resolve({}) }
        }
        xhr.onerror = () => reject(new Error('Network error'))
        xhr.open('POST', getLocalApiUrl('/api/gaming/egms/readings/upload'))
        xhr.send(fd)
      })
      setMeterResult(result)
      if (result?.inserted != null) toast.success(`Meter: ${result.inserted} rows imported`)
    } catch (e) {
      setMeterError(e.message)
      toast.error(e.message)
    } finally {
      setMeterUploading(false)
      setMeterProgress(0)
    }
  }, [meterTemplateId])

  const uploadPatron = React.useCallback(async (file) => {
    if (!file) return
    setPatronUploading(true); setPatronProgress(0); setPatronError(null); setPatronResult(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      if (patronTemplateId) fd.append('template_id', String(patronTemplateId))
      const xhr = new XMLHttpRequest()
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) setPatronProgress(Math.round((e.loaded / e.total) * 100)) }
      const result = await new Promise((resolve, reject) => {
        xhr.onload = () => {
          try {
            const j = JSON.parse(xhr.responseText || '{}')
            if (j.success !== false) resolve(j)
            else reject(new Error(j.error || 'Upload failed'))
          } catch (_) { resolve({}) }
        }
        xhr.onerror = () => reject(new Error('Network error'))
        xhr.open('POST', getLocalApiUrl('/api/patrons/upload'))
        xhr.send(fd)
      })
      setPatronResult(result)
      if (result && typeof result.imported === 'number') {
        toast.success(`Patrons: ${result.imported} new, ${result.updated ?? 0} updated, ${result.skipped ?? 0} skipped`)
      }
    } catch (e) {
      setPatronError(e.message)
      toast.error(e.message)
    } finally {
      setPatronUploading(false)
      setPatronProgress(0)
    }
  }, [patronTemplateId])

  const uploadEgm = React.useCallback(async (file) => {
    if (!file) return
    setEgmUploading(true); setEgmProgress(0); setEgmError(null); setEgmResult(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      if (egmTemplateId) fd.append('template_id', String(egmTemplateId))
      const xhr = new XMLHttpRequest()
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) setEgmProgress(Math.round((e.loaded / e.total) * 100)) }
      const result = await new Promise((resolve, reject) => {
        xhr.onload = () => {
          try {
            const j = JSON.parse(xhr.responseText || '{}')
            if (j.success !== false) resolve(j)
            else reject(new Error(j.error || 'Upload failed'))
          } catch (_) { resolve({}) }
        }
        xhr.onerror = () => reject(new Error('Network error'))
        xhr.open('POST', getLocalApiUrl('/api/egms/upload'))
        xhr.send(fd)
      })
      setEgmResult(result)
      if (result?.upserts != null) {
        const denoms = result.denominations_synced ?? 0
        toast.success(`EGM Registry: ${result.upserts} upserts, ${denoms} denominations synced`)
      }
    } catch (e) {
      setEgmError(e.message)
      toast.error(e.message)
    } finally {
      setEgmUploading(false)
      setEgmProgress(0)
    }
  }, [egmTemplateId])

  const uploadSessions = React.useCallback(async (file) => {
    if (!file) return
    setSessionsUploading(true); setSessionsProgress(0); setSessionsError(null); setSessionsResult(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      if (sessionsTemplateId) fd.append('template_id', String(sessionsTemplateId))
      const xhr = new XMLHttpRequest()
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) setSessionsProgress(Math.round((e.loaded / e.total) * 100)) }
      const result = await new Promise((resolve, reject) => {
        xhr.onload = () => {
          try {
            const j = JSON.parse(xhr.responseText || '{}')
            if (j.success !== false) resolve(j)
            else reject(new Error(j.error || 'Upload failed'))
          } catch (_) { resolve({}) }
        }
        xhr.onerror = () => reject(new Error('Network error'))
        xhr.open('POST', getLocalApiUrl('/api/gaming/sessions/upload'))
        xhr.send(fd)
      })
      setSessionsResult(result)
      if (result?.inserted != null) toast.success(`Sessions: ${result.inserted} rows imported`)
    } catch (e) {
      setSessionsError(e.message)
      toast.error(e.message)
    } finally {
      setSessionsUploading(false)
      setSessionsProgress(0)
    }
  }, [sessionsTemplateId])

  const renderMappingTable = (template) => {
    if (!template?.template_content) return null
    const c = template.template_content
    const mappings = c.mappings || {}
    const sourceLabels = c.sourceLabels || {}
    const fmt = c.sourceFormat || 'excel'
    const rows = Object.entries(mappings).filter(([, v]) => v != null && String(v).trim() !== '')
    if (rows.length === 0) return <p className="text-sm text-gray-500">No mappings defined</p>
    return (
      <div className="overflow-x-auto rounded border border-gray-200 text-sm">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-700">PSG field</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Source ({fmt === 'pipe-delimited' ? 'venue label' : 'header'})</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(([psg, src]) => (
              <tr key={psg}>
                <td className="px-3 py-1.5 font-mono text-gray-800">{psg}</td>
                <td className="px-3 py-1.5 text-gray-600">{src}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-3">Import Wizard</h3>
        <p className="text-sm text-gray-600 mb-4">
          Upload meter readings and gaming sessions separately. Supports .txt (pipe-delimited), .csv, and .xlsx.
          {GAMING_SOURCE_TABS_NOTE} {GAMING_COMBINED_NOTE}
        </p>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Date range (context)</label>
            <div className="flex gap-2">
              <input type="datetime-local" className="input-field flex-1" value={dateRange.from} onChange={(e)=>setDateRange(p=>({...p,from:e.target.value}))} />
              <input type="datetime-local" className="input-field flex-1" value={dateRange.to} onChange={(e)=>setDateRange(p=>({...p,to:e.target.value}))} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-2">1. Meter Readings</h3>
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Template</label>
            <select
              className="input-field w-full max-w-md"
              value={meterTemplateId}
              onChange={(e) => setMeterTemplateId(e.target.value)}
              disabled={loading}
            >
              <option value="">— None (Excel only) —</option>
              {meterTemplates.map((t) => (
                <option key={t.id} value={t.id}>{t.template_name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {meterTemplates.length === 0 && !loading ? 'No templates. Run migration 069 or create in Configure → Import Mapping.' : 'txt/csv require a template. Create in Configure → Import Mapping.'}
            </p>
          </div>
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <>
              {meterTemplate && (
                <details className="mb-4">
                  <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800">Show mapping</summary>
                  <div className="mt-2">{renderMappingTable(meterTemplate)}</div>
                </details>
              )}
              <div className="flex items-center gap-2">
                <input type="file" id="import-meter" className="hidden" accept=".txt,.csv,.xlsx,.xls" onChange={(e)=>{ const f=e.target.files?.[0]; if (f) uploadMeter(f); e.target.value='' }} />
                <label htmlFor="import-meter" className="btn-primary cursor-pointer">Upload meter file</label>
                <span className="text-sm text-gray-500">{meterUploading ? `Uploading ${meterProgress}%` : meterResult ? `${meterResult.inserted ?? 0} imported` : ''}</span>
              </div>
              {meterError && <p className="text-sm text-red-600 mt-2">{meterError}</p>}
            </>
          )}
        </div>

        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-2">2. Gaming Sessions</h3>
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Template</label>
            <select
              className="input-field w-full max-w-md"
              value={sessionsTemplateId}
              onChange={(e) => setSessionsTemplateId(e.target.value)}
              disabled={loading}
            >
              <option value="">— None (Excel only) —</option>
              {sessionsTemplates.map((t) => (
                <option key={t.id} value={t.id}>{t.template_name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {sessionsTemplates.length === 0 && !loading ? 'No templates. Run migration 069 or create in Configure → Import Mapping.' : 'txt/csv require a template. Create in Configure → Import Mapping.'}
            </p>
          </div>
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <>
              {sessionsTemplate && (
                <details className="mb-4">
                  <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800">Show mapping</summary>
                  <div className="mt-2">{renderMappingTable(sessionsTemplate)}</div>
                </details>
              )}
              <div className="flex items-center gap-2">
                <input type="file" id="import-sessions" className="hidden" accept=".txt,.csv,.xlsx,.xls" onChange={(e)=>{ const f=e.target.files?.[0]; if (f) uploadSessions(f); e.target.value='' }} />
                <label htmlFor="import-sessions" className="btn-primary cursor-pointer">Upload sessions file</label>
                <span className="text-sm text-gray-500">{sessionsUploading ? `Uploading ${sessionsProgress}%` : sessionsResult ? `${sessionsResult.inserted ?? 0} imported` : ''}</span>
              </div>
              {sessionsError && <p className="text-sm text-red-600 mt-2">{sessionsError}</p>}
            </>
          )}
        </div>

        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-2">3. Patron Directory (DimPatron)</h3>
          <p className="text-xs text-gray-500 mb-3">
            Different gaming machine vendors ship DimPatron files with different column layouts.
            Select or create a mapping template to drive the import.
          </p>
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Template</label>
            <select
              className="input-field w-full max-w-md"
              value={patronTemplateId}
              onChange={(e) => setPatronTemplateId(e.target.value)}
              disabled={loading}
            >
              <option value="">— None (Excel with headers only) —</option>
              {patronTemplates.map((t) => (
                <option key={t.id} value={t.id}>{t.template_name}{t.gaming_machine_vendor ? ` — ${t.gaming_machine_vendor}` : ''}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {patronTemplates.length === 0 && !loading ? 'No patron templates. Create one in Configure → Import Mapping (Patron).' : 'txt/csv require a template. Excel files with headers work without a template.'}
            </p>
          </div>
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <>
              {patronTemplate && (
                <details className="mb-4">
                  <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800">Show mapping</summary>
                  <div className="mt-2">{renderMappingTable(patronTemplate)}</div>
                </details>
              )}
              <div className="flex items-center gap-2">
                <input type="file" id="import-patron" className="hidden" accept=".txt,.csv,.xlsx,.xls" onChange={(e)=>{ const f=e.target.files?.[0]; if (f) uploadPatron(f); e.target.value='' }} />
                <label htmlFor="import-patron" className="btn-primary cursor-pointer">Upload DimPatron file</label>
                <span className="text-sm text-gray-500">
                  {patronUploading
                    ? `Uploading ${patronProgress}%`
                    : patronResult && typeof patronResult.imported === 'number'
                      ? `${patronResult.imported} new · ${patronResult.updated ?? 0} updated · ${patronResult.skipped ?? 0} skipped`
                      : ''}
                </span>
              </div>
              {patronError && <p className="text-sm text-red-600 mt-2">{patronError}</p>}
            </>
          )}
        </div>

        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-2">4. EGM Registry (DimEgm)</h3>
          <p className="text-xs text-gray-500 mb-3">
            Daily DimMachine file: denom, zone, status, install/deinstall dates. Merges onto rows bootstrapped from the
            client machine master list in <strong>Group Configuration → EGMs</strong>. Denominations sync to game_denominations.
          </p>
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Template</label>
            <select
              className="input-field w-full max-w-md"
              value={egmTemplateId}
              onChange={(e) => setEgmTemplateId(e.target.value)}
              disabled={loading}
            >
              <option value="">— None (Excel with headers only) —</option>
              {egmTemplates.map((t) => (
                <option key={t.id} value={t.id}>{t.template_name}{t.gaming_machine_vendor ? ` — ${t.gaming_machine_vendor}` : ''}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {egmTemplates.length === 0 && !loading ? 'No EGM templates. Create one in Configure → Import Mapping (EGM).' : 'txt/csv require a template. Excel files with headers work without a template.'}
            </p>
          </div>
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <>
              {egmTemplate && (
                <details className="mb-4">
                  <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800">Show mapping</summary>
                  <div className="mt-2">{renderMappingTable(egmTemplate)}</div>
                </details>
              )}
              <div className="flex items-center gap-2">
                <input type="file" id="import-egm" className="hidden" accept=".txt,.csv,.xlsx,.xls" onChange={(e)=>{ const f=e.target.files?.[0]; if (f) uploadEgm(f); e.target.value='' }} />
                <label htmlFor="import-egm" className="btn-primary cursor-pointer">Upload DimMachine file</label>
                <span className="text-sm text-gray-500">
                  {egmUploading
                    ? `Uploading ${egmProgress}%`
                    : egmResult && typeof egmResult.upserts === 'number'
                      ? `${egmResult.upserts} upserts · ${egmResult.denominations_synced ?? 0} denoms synced`
                      : ''}
                </span>
              </div>
              {egmError && <p className="text-sm text-red-600 mt-2">{egmError}</p>}
            </>
          )}
        </div>
      </div>

      <div className="card bg-psg-teal/5 border border-psg-teal/20">
        <p className="text-sm text-gray-700">
          <strong>Next:</strong> After importing meter, sessions, slot analysis, and EGM registry, open <strong>Combined</strong>, set the trading day, and click <strong>Process &amp; Combine</strong>. That run enriches sessions (meter deltas in the session window, slot-analysis jackpots, EGM/denomination, patron risk / rapid / extended play) into <strong>gaming_session_enriched</strong>, then allocates meter intervals into <strong>gaming_session_interval_attribution</strong> (carded + virtual uncarded). Use <strong>Member × Day</strong> for day rollups — expand a member for chronological sessions and cumulative totals; use <strong>By Session</strong> for the full floor timeline.
        </p>
      </div>
    </div>
  )
}

/** PG/MySQL row keys vary (aliases vs SELECT *); keep Combined tab resilient. */
function combinedIntervalStart(row) {
  if (!row || typeof row !== 'object') return null
  return row.IntervalStart ?? row.intervalstart ?? row.interval_start ?? null
}

const COMBINED_LIMIT_RECENT = 100
/** When a date range is set, allow enough rows for that window (server caps per request). */
const COMBINED_LIMIT_FILTERED = 5000
/** By Session page size — not the day total. */
const COMBINED_SESSION_PAGE_SIZE = 100

/** Booleans from PG/API may be true/false/1/0 */
function enrichFlagYes(v) {
  return v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true'
}

function parseFocusEgmpks(initial) {
  const fromCsv = String(initial?.egmpks || '').split(',').map((s) => s.trim()).filter(Boolean)
  if (fromCsv.length) return fromCsv
  const single = String(initial?.egm_pk || '').trim()
  return single ? [single] : []
}

function parseFocusHours(initial) {
  return String(initial?.hours || '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '')
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n) && n >= 0 && n <= 23)
}

function CombinedTab({ initialFilters }) {
  const [items, setItems] = React.useState([])
  const [totalSessions, setTotalSessions] = React.useState(null)
  const [totalIntervals, setTotalIntervals] = React.useState(null)
  const [sessionPage, setSessionPage] = React.useState(1)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [emptyHint, setEmptyHint] = React.useState(null)
  const [combineSummary, setCombineSummary] = React.useState(null)
  const [pipelineStatus, setPipelineStatus] = React.useState(null)
  const [pipelineLoading, setPipelineLoading] = React.useState(false)
  const [combineBusy, setCombineBusy] = React.useState(false)
  const [combineProgress, setCombineProgress] = React.useState(null) // { step, total, label, pct }
  const [dayTotals, setDayTotals] = React.useState(null)
  const [dayTotalsLoading, setDayTotalsLoading] = React.useState(false)
  const [unallocAnalysis, setUnallocAnalysis] = React.useState(null)
  const [unallocLoading, setUnallocLoading] = React.useState(false)
  const [unallocOpen, setUnallocOpen] = React.useState(false)
  const [expandedSessions, setExpandedSessions] = React.useState(() => new Set())
  const [expandedMembers, setExpandedMembers] = React.useState(() => new Set())
  const [memberSessionCache, setMemberSessionCache] = React.useState({})
  const loadSeqRef = React.useRef(0)
  const [filters, setFilters] = React.useState(() => ({
    egm_pk: initialFilters?.egm_pk || '',
    egmpks: initialFilters?.egmpks || '',
    member_id: initialFilters?.member_id || '',
    from: initialFilters?.trading_day ? '' : (initialFilters?.from || ''),
    to: initialFilters?.trading_day ? '' : (initialFilters?.to || ''),
    trading_day: initialFilters?.trading_day || '',
    view: initialFilters?.view || 'intervals',
    player_type: initialFilters?.player_type || 'all',
    machine_class: initialFilters?.machine_class || 'all',
    hours: initialFilters?.hours || '',
    has_jackpot: initialFilters?.has_jackpot || false,
    has_cash_in: initialFilters?.has_cash_in || false,
    min_turnover: initialFilters?.min_turnover || '',
    net_result: initialFilters?.net_result || 'all',
    min_net_gain: initialFilters?.min_net_gain || '',
    cutoff_hour: initialFilters?.cutoff_hour || '3',
  }))
  const [chartSelectedEgmpks, setChartSelectedEgmpks] = React.useState(() => parseFocusEgmpks(initialFilters))
  const [chartSelectedHours, setChartSelectedHours] = React.useState(() => parseFocusHours(initialFilters))
  const [reconReport, setReconReport] = React.useState(null)
  const [reconLoading, setReconLoading] = React.useState(false)
  const prevTradingDayRef = React.useRef(filters.trading_day)

  const formatMoney = React.useCallback((n) => {
    // Attribution turnover_alloc is meter counter units (cents) — same as Daily Turnover.
    return formatMeterCents(n)
  }, [])

  const toggleSessionExpand = React.useCallback((sessionPk) => {
    setExpandedSessions((prev) => {
      const next = new Set(prev)
      if (next.has(sessionPk)) next.delete(sessionPk)
      else next.add(sessionPk)
      return next
    })
  }, [])

  const loadMemberSessions = React.useCallback(async (memberId, tradingDay) => {
    const cacheKey = memberDayCacheKey(memberId, tradingDay)
    setMemberSessionCache((prev) => ({
      ...prev,
      [cacheKey]: { ...(prev[cacheKey] || {}), loading: true, error: null },
    }))
    try {
      const params = new URLSearchParams({
        member_id: String(memberId),
        trading_day: String(tradingDay).slice(0, 10),
        include_intervals: 'true',
        limit: '500',
        page: '1',
      })
      const r = await authFetch(getLocalApiUrl(`/api/gaming/combined/by-session?${params}`))
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed to load member sessions')
      const sessions = annotateMemberDaySessionCumulative(Array.isArray(j.data) ? j.data : [])
      setMemberSessionCache((prev) => ({
        ...prev,
        [cacheKey]: { loading: false, error: null, sessions },
      }))
    } catch (err) {
      setMemberSessionCache((prev) => ({
        ...prev,
        [cacheKey]: { loading: false, error: err?.message || 'Failed to load sessions', sessions: [] },
      }))
    }
  }, [])

  const toggleMemberExpand = React.useCallback((row) => {
    const memberId = row?.MemberID ?? row?.memberid
    const tradingDay = memberDayTradingDayParam(row, filters.trading_day)
    const cacheKey = memberDayCacheKey(memberId, tradingDay)
    setExpandedMembers((prev) => {
      const next = new Set(prev)
      const opening = !next.has(cacheKey)
      if (opening) {
        next.add(cacheKey)
        setMemberSessionCache((cachePrev) => {
          const cached = cachePrev[cacheKey]
          if (!cached?.sessions?.length && !cached?.loading) {
            loadMemberSessions(memberId, tradingDay)
          }
          return cachePrev
        })
      } else {
        next.delete(cacheKey)
      }
      return next
    })
  }, [filters.trading_day, loadMemberSessions])

  const resetChartFocus = React.useCallback(() => {
    setChartSelectedEgmpks([])
    setChartSelectedHours([])
  }, [])

  const toggleChartEgm = React.useCallback((egmpk) => {
    const key = String(egmpk || '').trim()
    if (!key) return
    setChartSelectedEgmpks((prev) => (
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    ))
  }, [])

  const toggleChartHour = React.useCallback((hour) => {
    const h = Number(hour)
    if (!Number.isFinite(h) || h < 0 || h > 23) return
    setChartSelectedHours((prev) => (
      prev.includes(h) ? prev.filter((x) => x !== h) : [...prev, h].sort((a, b) => a - b)
    ))
  }, [])

  React.useEffect(() => {
    if (prevTradingDayRef.current !== filters.trading_day) {
      resetChartFocus()
      prevTradingDayRef.current = filters.trading_day
      // Calendar From/To windows exclude intervals whose UTC timestamps spill past midnight.
      setFilters((f) => ((f.from || f.to) ? { ...f, from: '', to: '' } : f))
    }
  }, [filters.trading_day, resetChartFocus])

  // Stale By Session thresholds (Min TO / Min gain) often hide every row after changing house or day.
  const prevEgmDayRef = React.useRef({ egm_pk: filters.egm_pk, trading_day: filters.trading_day })
  React.useEffect(() => {
    const prev = prevEgmDayRef.current
    const egmChanged = String(prev.egm_pk || '') !== String(filters.egm_pk || '')
    const dayChanged = String(prev.trading_day || '') !== String(filters.trading_day || '')
    prevEgmDayRef.current = { egm_pk: filters.egm_pk, trading_day: filters.trading_day }
    if (filters.view !== 'sessions' || (!egmChanged && !dayChanged)) return
    setFilters((f) => {
      if (!f.has_cash_in && !String(f.min_turnover || '').trim() && !String(f.min_net_gain || '').trim() && (f.net_result || 'all') === 'all') {
        return f
      }
      return {
        ...f,
        has_cash_in: false,
        min_turnover: '',
        min_net_gain: '',
        net_result: 'all',
      }
    })
  }, [filters.egm_pk, filters.trading_day, filters.view])

  const loadReconciliation = React.useCallback(async () => {
    const day = (filters.trading_day || '').trim()
    if (!day) {
      setReconReport(null)
      return
    }
    setReconLoading(true)
    try {
      const data = await fetchMeterReconciliation(
        day,
        reconChartFilters({
          machineClass: filters.machine_class,
          selectedEgmpks: chartSelectedEgmpks,
          selectedHours: chartSelectedHours,
        })
      )
      setReconReport(data)
    } catch (_) {
      setReconReport(null)
    } finally {
      setReconLoading(false)
    }
  }, [filters.trading_day, filters.machine_class, chartSelectedEgmpks, chartSelectedHours])

  React.useEffect(() => { loadReconciliation() }, [loadReconciliation])

  const chartAllocateLabel = React.useMemo(() => {
    const hasMeter = (reconReport?.reconciliation?.completeness?.distinct_egms || 0) > 0
    if (!hasMeter) return null
    const attributed =
      (pipelineStatus?.attribution_count ?? 0) > 0 ||
      (dayTotals?.attribution?.total_rows ?? 0) > 0
    return attributed ? 'Allocated' : 'Meter only — allocate not run'
  }, [reconReport, pipelineStatus, dayTotals])

  const loadDayTotals = React.useCallback(async () => {
    const day = (filters.trading_day || '').trim()
    if (!day) {
      setDayTotals(null)
      return
    }
    try {
      setDayTotalsLoading(true)
      const qs = new URLSearchParams({ trading_day: day })
      const r = await authFetch(getLocalApiUrl(`/api/gaming/combined/day-totals?${qs}`))
      const j = await r.json()
      if (j.success) setDayTotals(j.data)
      else setDayTotals(null)
    } catch (_) {
      setDayTotals(null)
    } finally {
      setDayTotalsLoading(false)
    }
  }, [filters.trading_day])

  React.useEffect(() => { loadDayTotals() }, [loadDayTotals])

  const loadUnallocatedAnalysis = React.useCallback(async () => {
    const day = (filters.trading_day || '').trim()
    if (!day) {
      setUnallocAnalysis(null)
      return
    }
    try {
      setUnallocLoading(true)
      const qs = new URLSearchParams({ trading_day: day, limit: '40' })
      const r = await authFetch(getLocalApiUrl(`/api/gaming/combined/unallocated-analysis?${qs}`))
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed to analyse unallocated')
      setUnallocAnalysis(j.data)
      setUnallocOpen(true)
    } catch (e) {
      toast.error(e.message || 'Unallocated analysis failed')
      setUnallocAnalysis(null)
    } finally {
      setUnallocLoading(false)
    }
  }, [filters.trading_day])

  React.useEffect(() => {
    setUnallocAnalysis(null)
    setUnallocOpen(false)
  }, [filters.trading_day])

  const parseDate = React.useCallback((val) => {
    if (!val) return null
    try {
      const s = typeof val === 'string' ? (val.includes('T') ? val : val.replace(' ', 'T')) : val
      const d = new Date(s)
      return isNaN(d) ? null : d
    } catch { return null }
  }, [])

  const load = React.useCallback(async ()=>{
    const seq = ++loadSeqRef.current
    try {
      setLoading(true); setError(null); setEmptyHint(null)
      const { view, ...rest } = filters
      const tradingDaySet = Boolean((rest.trading_day || '').trim())
      const hasDateRange = Boolean((rest.from || '').trim()) || Boolean((rest.to || '').trim()) || tradingDaySet
      const params = {
        page: view === 'sessions' ? String(sessionPage) : '1',
        limit: String(
          view === 'sessions'
            ? COMBINED_SESSION_PAGE_SIZE
            : (hasDateRange ? COMBINED_LIMIT_FILTERED : COMBINED_LIMIT_RECENT)
        ),
      }
      // Trading day defines the business day; calendar From/To often hides all rows (UTC spill).
      if (rest.from && !tradingDaySet) params.from = toApiDateTimeParam(rest.from)
      if (rest.to && !tradingDaySet) params.to = toApiDateTimeParam(rest.to)
      if (rest.trading_day) params.trading_day = rest.trading_day
      if (rest.cutoff_hour) params.cutoff_hour = rest.cutoff_hour
      const focusEgmpks = chartSelectedEgmpks.length
        ? chartSelectedEgmpks
        : String(rest.egmpks || '').split(',').map((s) => s.trim()).filter(Boolean)
      const focusHours = chartSelectedHours.length
        ? chartSelectedHours
        : String(rest.hours || '')
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s !== '')
            .map((s) => Number(s))
            .filter((n) => Number.isFinite(n) && n >= 0 && n <= 23)
      const typedEgm = String(rest.egm_pk || '').trim()
      // Typed House / machine ID wins over chart focus chips (otherwise filter looks ignored).
      if (typedEgm) params.egm_pk = typedEgm
      else if (focusEgmpks.length) params.egmpks = focusEgmpks.join(',')
      // Typed house / machine ID wins over chart hour chips too (hour focus + EGM search = empty table).
      if (!typedEgm && focusHours.length) params.hours = focusHours.map(String).join(',')
      if (rest.member_id) params.member_id = rest.member_id
      if (rest.player_type && rest.player_type !== 'all') params.player_type = rest.player_type
      if (rest.machine_class && rest.machine_class !== 'all') params.machine_class = rest.machine_class
      if (rest.has_jackpot) params.has_jackpot = 'true'
      if (view === 'sessions') {
        params.include_intervals = 'true'
        if (rest.has_cash_in) params.has_cash_in = 'true'
        const minTo = String(rest.min_turnover || '').trim()
        if (minTo && Number(minTo) > 0) params.min_turnover = minTo
        if (rest.net_result && rest.net_result !== 'all') params.net_result = rest.net_result
        const minGain = String(rest.min_net_gain || '').trim()
        if (minGain && Number(minGain) > 0) params.min_net_gain = minGain
      }
      const qs = new URLSearchParams(params).toString()
      const endpoint =
        view === 'summary' ? '/api/gaming/combined/summary'
        : view === 'sessions' ? '/api/gaming/combined/by-session'
        : '/api/gaming/combined'
      const url = getLocalApiUrl(`${endpoint}${qs ? '?' + qs : ''}`)
      if (import.meta.env?.DEV) console.log('[Combined] fetch:', url, 'params:', params)
      const r = await authFetch(url)
      const j = await r.json()
      if (seq !== loadSeqRef.current) return
      if (!j.success) throw new Error(j.error || 'Failed to load combined data')
      const rows = Array.isArray(j.data) ? j.data : []
      // Store raw rows; display applies session filters so stale races cannot show wrong data
      setItems(rows)
      setExpandedSessions(new Set())
      setExpandedMembers(new Set())
      setMemberSessionCache({})
      if (view === 'sessions' && j.total != null) setTotalSessions(Number(j.total) || 0)
      else setTotalSessions(null)
      if (view === 'intervals' && j.total != null) setTotalIntervals(Number(j.total) || rows.length)
      else setTotalIntervals(null)
      if (j.hint && rows.length === 0) setEmptyHint(j.hint)
    } catch (e) {
      if (seq !== loadSeqRef.current) return
      setError(e.message)
      setItems([])
      setTotalSessions(null)
    } finally {
      if (seq === loadSeqRef.current) setLoading(false)
    }
  }, [filters, sessionPage, chartSelectedEgmpks, chartSelectedHours])

  React.useEffect(()=>{ load() }, [load])

  // Reset to page 1 when filters/view change (not when only page changes)
  React.useEffect(() => {
    setSessionPage(1)
  }, [
    filters.view,
    filters.egm_pk,
    filters.egmpks,
    filters.hours,
    chartSelectedEgmpks,
    chartSelectedHours,
    filters.member_id,
    filters.from,
    filters.to,
    filters.trading_day,
    filters.player_type,
    filters.machine_class,
    filters.has_jackpot,
    filters.has_cash_in,
    filters.min_turnover,
    filters.net_result,
    filters.min_net_gain,
    filters.cutoff_hour,
  ])

  // Always enforce By Session filters against current filter state (fixes race / stale responses)
  const displayedItems = React.useMemo(() => {
    let next = items
    if (filters.view === 'sessions') next = applyCombinedSessionFilters(items, filters)
    else if (filters.has_jackpot) next = applyCombinedSessionFilters(items, { has_jackpot: true })
    if (filters.view === 'sessions' || filters.view === 'intervals') {
      next = sortCombinedRowsChronologically(next, filters.view)
    }
    return next
  }, [items, filters])

  const sessionPageCount = React.useMemo(() => {
    if (totalSessions == null) return 1
    return Math.max(1, Math.ceil(totalSessions / COMBINED_SESSION_PAGE_SIZE))
  }, [totalSessions])
  const loadPipelineStatus = React.useCallback(async (forceRefresh = false) => {
    try {
      setPipelineLoading(true)
      const day = (filters.trading_day || '').trim() || (filters.from || '').slice(0, 10)
      const params = new URLSearchParams({ include_jobs: 'false' })
      if (day) params.set('trading_day', day)
      if (forceRefresh) params.set('refresh', 'true')
      const r = await authFetch(getLocalApiUrl(`/api/gaming/pipeline/status?${params}`))
      const j = await r.json()
      if (j.success) setPipelineStatus(j.data)
    } catch (_) {
      setPipelineStatus(null)
    } finally {
      setPipelineLoading(false)
    }
  }, [filters.trading_day, filters.from])

  React.useEffect(() => { loadPipelineStatus() }, [loadPipelineStatus])

  // Clear stale rows when switching view to avoid showing previous layout's data
  React.useEffect(() => { setItems([]); setError(null) }, [filters.view])

  const chartFocusActive = chartSelectedEgmpks.length > 0 || chartSelectedHours.length > 0

  const bySessionFiltersActive = React.useMemo(() => {
    if (filters.view !== 'sessions') return []
    const parts = []
    if (filters.has_cash_in) parts.push('Cash in')
    const minTo = Number(filters.min_turnover)
    if (Number.isFinite(minTo) && minTo > 0) parts.push(`Min TO $${minTo}`)
    if (filters.net_result && filters.net_result !== 'all') parts.push(`Net ${filters.net_result}`)
    const minGain = Number(filters.min_net_gain)
    if (Number.isFinite(minGain) && minGain > 0) parts.push(`Min gain $${minGain}`)
    return parts
  }, [filters.view, filters.has_cash_in, filters.min_turnover, filters.net_result, filters.min_net_gain])

  const bySessionEmptyMessage = React.useMemo(() => {
    if (filters.view !== 'sessions' || displayedItems.length > 0) return null
    if (emptyHint) return emptyHint
    if (bySessionFiltersActive.length) {
      return `No sessions match By Session filters (${bySessionFiltersActive.join(' · ')}). Click Clear session filters above, or lower Min TO / Min gain — then Reload.`
    }
    if (String(filters.egm_pk || '').trim()) {
      const id = filters.egm_pk.trim()
      const focusNote = chartFocusActive ? ' Chart hour/EGM focus is ignored while House / machine ID is typed — click Clear focus on the chart if results still look wrong.' : ''
      return `No attributed sessions for ${id} on trading day ${filters.trading_day || '(not set)'}.${focusNote} Also try house 010096 for machine 1856, then Reload.`
    }
    return null
  }, [filters.view, displayedItems.length, emptyHint, bySessionFiltersActive, filters.egm_pk, filters.trading_day, chartFocusActive])

  const clearBySessionFilters = React.useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      has_cash_in: false,
      min_turnover: '',
      net_result: 'all',
      min_net_gain: '',
    }))
  }, [])

  // Only surface the pipeline banner when something needs attention (not on healthy ingest_ok / rules_ready)
  const showPipelineAlert = Boolean(pipelineStatus) && (() => {
    if (pipelineStatus.pipeline_state === 'running') return false
    if (pipelineStatus.last_error) return true
    if (!pipelineStatus.ingest_meter_ok || !pipelineStatus.ingest_sessions_ok) return true
    const state = String(pipelineStatus.pipeline_state || '')
    if (/fail|error|blocked|waiting_ingest/i.test(state)) return true
    return false
  })()

  return (
    <div>
      <div className="card mb-3 !p-3 border border-psg-teal/20 bg-psg-teal/5">
        <p className="text-sm text-gray-800">
          <strong>Combined only:</strong> enriched and attributed gaming data. Process &amp; Combine merges <strong>meter readings</strong>, <strong>carded sessions</strong>, <strong>slot analysis</strong>, and <strong>EGM registry</strong> (denomination, machine class, house remap) into enriched sessions plus interval attribution. Other tabs show source ingest only.
        </p>
      </div>
      {showPipelineAlert && (
        <div className={`card mb-3 !p-3 border ${pipelineStatus.last_error ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Pipeline: {pipelineStatus.pipeline_state?.replace(/_/g, ' ') || 'unknown'}</h3>
              <p className="text-xs text-gray-600 mt-1">
                Meter ingest: {pipelineStatus.ingest_meter_ok ? 'OK' : 'pending'} · Sessions: {pipelineStatus.ingest_sessions_ok ? 'OK' : 'pending'} ·
                Enriched: {pipelineStatus.enriched_count ?? 0} · Attribution: {pipelineStatus.attribution_count ?? 0}
              </p>
              {pipelineStatus.last_error && (
                <p className="text-xs text-red-700 mt-1">{pipelineStatus.last_error}</p>
              )}
            </div>
            <button
              type="button"
              className="btn-secondary !py-1.5 !px-3 !text-sm"
              disabled={pipelineLoading}
              onClick={() => loadPipelineStatus(true)}
            >
              Refresh status
            </button>
          </div>
        </div>
      )}
      <div className="card mb-3 !p-3">
        <div className="flex flex-wrap items-end gap-x-2 gap-y-2">
          <div className="min-w-[7rem] flex-1 basis-[7rem]">
            <label className="block text-[11px] font-medium text-gray-500 mb-0.5" title="Matches house number, bare floor (246), or DimMachine machine pk via EGM registry">House / machine ID</label>
            <input
              className="input-field !py-1.5 !px-2 !text-sm"
              placeholder={EGM_FILTER_PLACEHOLDER}
              value={filters.egm_pk}
              onChange={(e) => {
                const v = e.target.value
                setFilters((prev) => ({ ...prev, egm_pk: v }))
                if (String(v || '').trim()) {
                  setChartSelectedEgmpks([])
                  setChartSelectedHours([])
                }
              }}
            />
          </div>
          <div className="min-w-[9rem] flex-[1.2] basis-[9rem]">
            <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Member / Player</label>
            <input className="input-field !py-1.5 !px-2 !text-sm" placeholder="Member # or player id" value={filters.member_id} onChange={(e)=>setFilters(prev=>({ ...prev, member_id:e.target.value }))} />
          </div>
          <div className="min-w-[9.5rem] flex-1 basis-[9.5rem]">
            <label className="block text-[11px] font-medium text-gray-500 mb-0.5">View</label>
            <select className="input-field !py-1.5 !px-2 !text-sm" value={filters.view} onChange={(e)=>setFilters(prev=>({ ...prev, view:e.target.value }))}>
              <option value="intervals">Intervals</option>
              <option value="sessions">By Session</option>
              <option value="summary">Member × Day</option>
            </select>
          </div>
          <div className="min-w-[7rem] flex-1 basis-[7rem]">
            <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Players</label>
            <select
              className="input-field !py-1.5 !px-2 !text-sm"
              value={filters.player_type || 'all'}
              onChange={(e) => setFilters((prev) => ({ ...prev, player_type: e.target.value }))}
            >
              <option value="all">All</option>
              <option value="carded">Carded</option>
              <option value="uncarded">Uncarded</option>
            </select>
          </div>
          <div className="min-w-[7rem] flex-1 basis-[7rem]">
            <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Machine</label>
            <select
              className="input-field !py-1.5 !px-2 !text-sm"
              value={filters.machine_class || 'all'}
              onChange={(e) => setFilters((prev) => ({ ...prev, machine_class: e.target.value }))}
              title="From EGM registry DimMachine type (EGM vs MTGM)"
            >
              <option value="all">All types</option>
              <option value="EGM">EGM (normal)</option>
              <option value="MTGM">MTGM (multi)</option>
            </select>
          </div>
          <div className="min-w-[7.5rem] flex-1 basis-[7.5rem]">
            <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Jackpot</label>
            <select
              className="input-field !py-1.5 !px-2 !text-sm"
              value={filters.has_jackpot ? 'yes' : 'all'}
              onChange={(e) => setFilters((prev) => ({ ...prev, has_jackpot: e.target.value === 'yes' }))}
              title="Shows sits on EGMs that had Slot Analysis jackpot/progressive that day (and/or session ExternalWin). Big $ are usually EGM-day machine totals shared across every sit — not that session’s win."
            >
              <option value="all">All</option>
              <option value="yes">Has jackpot</option>
            </select>
          </div>
          {filters.view === 'sessions' && (
            <>
              <div className="min-w-[7rem] flex-1 basis-[7rem]">
                <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Cash in</label>
                <select
                  className="input-field !py-1.5 !px-2 !text-sm"
                  value={filters.has_cash_in ? 'yes' : 'all'}
                  onChange={(e) => setFilters((prev) => ({ ...prev, has_cash_in: e.target.value === 'yes' }))}
                  title="Physical cash / cashdrop &gt; 0"
                >
                  <option value="all">All</option>
                  <option value="yes">Has cash</option>
                </select>
              </div>
              <div className="min-w-[6.5rem] flex-1 basis-[6.5rem]">
                <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Min TO ($)</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="input-field !py-1.5 !px-2 !text-sm"
                  placeholder="1000"
                  value={filters.min_turnover || ''}
                  onChange={(e) => setFilters((prev) => ({ ...prev, min_turnover: e.target.value }))}
                  title="Min session turnover (AUD)"
                />
              </div>
              <div className="min-w-[8rem] flex-1 basis-[8rem]">
                <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Net (TO−Win)</label>
                <select
                  className="input-field !py-1.5 !px-2 !text-sm"
                  value={filters.net_result || 'all'}
                  onChange={(e) => setFilters((prev) => ({ ...prev, net_result: e.target.value }))}
                  title="Player loss = red; player gain = green"
                >
                  <option value="all">All</option>
                  <option value="loss">Loss (red)</option>
                  <option value="gain">Gain (green)</option>
                </select>
              </div>
              <div className="min-w-[6.5rem] flex-1 basis-[6.5rem]">
                <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Min gain ($)</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="input-field !py-1.5 !px-2 !text-sm"
                  placeholder="1000"
                  value={filters.min_net_gain || ''}
                  onChange={(e) => setFilters((prev) => ({ ...prev, min_net_gain: e.target.value }))}
                  title="Min player net gain Win−TO (AUD)"
                />
              </div>
            </>
          )}
          {filters.view==='summary' && (
            <div className="min-w-[5rem] basis-[5rem]">
              <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Cutoff hr</label>
              <input type="number" min="0" max="23" className="input-field !py-1.5 !px-2 !text-sm" value={filters.cutoff_hour} onChange={(e)=>setFilters(prev=>({ ...prev, cutoff_hour:e.target.value }))} />
            </div>
          )}
          <div className="min-w-[8.5rem] flex-1 basis-[8.5rem]">
            <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Trading day</label>
            <input
              type="date"
              className="input-field !py-1.5 !px-2 !text-sm"
              value={filters.trading_day || ''}
              onChange={(e) => setFilters((prev) => ({
                ...prev,
                trading_day: e.target.value,
                from: '',
                to: '',
              }))}
            />
          </div>
          <div className="min-w-[10rem] flex-[1.1] basis-[10rem]">
            <label className="block text-[11px] font-medium text-gray-500 mb-0.5">From</label>
            <input type="datetime-local" className="input-field !py-1.5 !px-2 !text-sm" value={filters.from} onChange={(e)=>setFilters(prev=>({ ...prev, from:e.target.value }))} />
          </div>
          <div className="min-w-[10rem] flex-[1.1] basis-[10rem]">
            <label className="block text-[11px] font-medium text-gray-500 mb-0.5">To</label>
            <input type="datetime-local" className="input-field !py-1.5 !px-2 !text-sm" value={filters.to} onChange={(e)=>setFilters(prev=>({ ...prev, to:e.target.value }))} />
          </div>
          <div className="shrink-0 pb-px">
            <button className="btn-primary !py-1.5 !px-3 !text-sm" onClick={load}>Reload</button>
          </div>
        </div>
        <p className="text-[11px] text-gray-500 mt-1.5 leading-snug">
          {filters.trading_day
            ? `Day ${filters.trading_day} · same gamingdate as Daily Turnover. From/To are ignored while Trading day is set.`
            : `Set trading day (preferred) or From/To to narrow results.`}
          {filters.view === 'sessions' && ' Session filters AND together; amounts in AUD.'}
        </p>
        {filters.view === 'sessions' && bySessionFiltersActive.length > 0 && (
          <div className="mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>
              By Session filters active ({bySessionFiltersActive.join(' · ')}) — all must match; most machines will show nothing with high Min TO / Min gain.
            </span>
            <button type="button" className="underline font-medium shrink-0" onClick={clearBySessionFilters}>
              Clear session filters
            </button>
          </div>
        )}
      </div>
      {filters.trading_day && (
        <div className="card mb-4 border border-slate-200 bg-slate-50">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="text-sm font-semibold text-gray-900">
              Day turnover · {filters.trading_day}
              {dayTotals?.source ? (
                <span className="ml-2 text-xs font-normal text-gray-500">
                  (from {dayTotals.source === 'attribution' ? 'combined attribution' : 'sessions'} · same trading day as Daily Turnover)
                </span>
              ) : null}
            </h3>
            {dayTotalsLoading && <span className="text-xs text-gray-500">Loading…</span>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-lg bg-white border border-indigo-200 px-4 py-3">
              <div className="text-[11px] uppercase tracking-wide text-indigo-700">Meter day (Daily Turnover)</div>
              <div className="text-xl font-semibold text-indigo-950 mt-1">{formatMoney(dayTotals?.meter_turnover)}</div>
              <div className="text-xs text-indigo-700 mt-1">Source of truth for floor TO</div>
            </div>
            <button
              type="button"
              className={`rounded-lg bg-white border px-4 py-3 text-left transition ${filters.player_type === 'carded' ? 'border-emerald-500 ring-1 ring-emerald-300' : 'border-emerald-200 hover:border-emerald-300'}`}
              onClick={() => setFilters((prev) => ({ ...prev, player_type: 'carded' }))}
              title="Filter to carded only"
            >
              <div className="text-[11px] uppercase tracking-wide text-emerald-700">Carded</div>
              <div className="text-xl font-semibold text-emerald-900 mt-1">{formatMoney(dayTotals?.carded_turnover)}</div>
              {dayTotals?.carded_pct != null && (
                <div className="text-xs text-emerald-700 mt-1">{Number(dayTotals.carded_pct).toFixed(1)}% of attributed</div>
              )}
            </button>
            <button
              type="button"
              className={`rounded-lg bg-white border px-4 py-3 text-left transition ${filters.player_type === 'uncarded' ? 'border-amber-500 ring-1 ring-amber-300' : 'border-amber-200 hover:border-amber-300'}`}
              onClick={() => setFilters((prev) => ({ ...prev, player_type: 'uncarded' }))}
              title="Filter to uncarded only"
            >
              <div className="text-[11px] uppercase tracking-wide text-amber-700">Uncarded</div>
              <div className="text-xl font-semibold text-amber-900 mt-1">{formatMoney(dayTotals?.uncarded_turnover)}</div>
              {dayTotals?.uncarded_pct != null && (
                <div className="text-xs text-amber-700 mt-1">{Number(dayTotals.uncarded_pct).toFixed(1)}% of attributed</div>
              )}
            </button>
            <button
              type="button"
              className={`rounded-lg bg-white border px-4 py-3 text-left transition ${unallocOpen ? 'border-rose-500 ring-1 ring-rose-300' : 'border-rose-200 hover:border-rose-300'}`}
              title="Analyse unallocated by EGM"
              onClick={() => {
                if (unallocOpen && unallocAnalysis) {
                  setUnallocOpen(false)
                  return
                }
                loadUnallocatedAnalysis()
              }}
            >
              <div className="text-[11px] uppercase tracking-wide text-rose-700">Unallocated</div>
              <div className="text-xl font-semibold text-rose-900 mt-1">{formatMoney(dayTotals?.unallocated_turnover)}</div>
              {dayTotals?.unallocated_pct != null && (
                <div className="text-xs text-rose-700 mt-1">{Number(dayTotals.unallocated_pct).toFixed(1)}% of meter day</div>
              )}
              <div className="text-[11px] text-rose-600 mt-2">
                {unallocLoading ? 'Analysing…' : unallocOpen ? 'Hide EGM breakdown ▴' : 'Click for EGM breakdown ▾'}
              </div>
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Attributed (carded + uncarded): {formatMoney(dayTotals?.total_turnover)}
            {dayTotals?.attributed_of_meter_pct != null
              ? ` · ${Number(dayTotals.attributed_of_meter_pct).toFixed(1)}% of meter day`
              : ''}
            {dayTotals?.sessions
              ? ` · Sessions: ${dayTotals.sessions.carded_sessions ?? 0} carded · ${dayTotals.sessions.uncarded_sessions ?? 0} uncarded`
              : ''}
            {dayTotals?.attribution?.total_rows != null
              ? ` · Attribution rows: ${dayTotals.attribution.carded_rows ?? 0} carded · ${dayTotals.attribution.uncarded_rows ?? 0} uncarded`
              : ''}
          </p>
          {unallocOpen && unallocAnalysis && (
            <div className="mt-4 rounded-lg border border-rose-100 bg-white p-3">
              <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">Unallocated by EGM</h4>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {unallocAnalysis.egms_with_unallocated ?? 0} EGMs with a gap · top {unallocAnalysis.top_egms?.length ?? 0} shown
                    {unallocAnalysis.top_egms_share_of_unallocated != null
                      ? ` · cover ${Number(unallocAnalysis.top_egms_share_of_unallocated).toFixed(0)}% of unallocated`
                      : ''}
                  </p>
                </div>
                <button type="button" className="btn-secondary text-xs" disabled={unallocLoading} onClick={loadUnallocatedAnalysis}>
                  Refresh analysis
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3 text-xs">
                <div className="rounded border border-gray-100 bg-slate-50 px-2 py-1.5">
                  <div className="text-gray-500">Scope gap (meter day not seen by pipeline)</div>
                  <div className="font-semibold text-gray-900">{formatMoney(unallocAnalysis.hints?.scope_gap)}</div>
                  {unallocAnalysis.hints?.scope_gap_pct_of_meter_day != null && (
                    <div className="text-gray-500">{Number(unallocAnalysis.hints.scope_gap_pct_of_meter_day).toFixed(1)}% of meter day</div>
                  )}
                </div>
                <div className="rounded border border-gray-100 bg-slate-50 px-2 py-1.5">
                  <div className="text-gray-500">Attribution gap (seen but not bucketed)</div>
                  <div className="font-semibold text-gray-900">{formatMoney(unallocAnalysis.hints?.attribution_gap)}</div>
                  {unallocAnalysis.hints?.attribution_gap_pct_of_meter_day != null && (
                    <div className="text-gray-500">{Number(unallocAnalysis.hints.attribution_gap_pct_of_meter_day).toFixed(1)}% of meter day</div>
                  )}
                </div>
                <div className="rounded border border-gray-100 bg-slate-50 px-2 py-1.5">
                  <div className="text-gray-500">Over-attributed (vs delta set)</div>
                  <div className="font-semibold text-gray-900">{formatMoney(unallocAnalysis.hints?.over_attributed_turnover)}</div>
                  <div className="text-gray-500">{unallocAnalysis.hints?.egms_with_resets ?? 0} with meter reset</div>
                </div>
              </div>
              <p className="text-[11px] text-gray-500 mb-2">{unallocAnalysis.hints?.note}</p>
              <div className="overflow-x-auto max-h-80 overflow-y-auto rounded border border-gray-100">
                <table className="min-w-full text-xs">
                  <thead className="bg-rose-50 text-rose-900 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left">EgmPK</th>
                      <th className="px-2 py-1.5 text-right">Meter</th>
                      <th className="px-2 py-1.5 text-right">Attributed</th>
                      <th className="px-2 py-1.5 text-right">Unallocated</th>
                      <th className="px-2 py-1.5 text-right">Uncarded</th>
                      <th className="px-2 py-1.5 text-left">Flags</th>
                      <th className="px-2 py-1.5 text-left"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {(unallocAnalysis.top_egms || []).map((row) => (
                      <tr key={row.egmpk} className="hover:bg-rose-50/40">
                        <td className="px-2 py-1.5 font-mono">{row.egmpk}</td>
                        <td className="px-2 py-1.5 text-right">{formatMoney(row.meter_turnover)}</td>
                        <td className="px-2 py-1.5 text-right">{formatMoney(row.attributed_turnover)}</td>
                        <td className="px-2 py-1.5 text-right font-medium text-rose-800">{formatMoney(row.unallocated_turnover)}</td>
                        <td className="px-2 py-1.5 text-right">{formatMoney(row.uncarded_turnover)}</td>
                        <td className="px-2 py-1.5 text-gray-600">{(row.reasons || []).join(', ') || '—'}</td>
                        <td className="px-2 py-1.5">
                          <button
                            type="button"
                            className="text-psg-teal underline"
                            onClick={() => setFilters((prev) => ({ ...prev, egm_pk: row.egmpk, view: 'sessions' }))}
                          >
                            Open
                          </button>
                        </td>
                      </tr>
                    ))}
                    {(!unallocAnalysis.top_egms || unallocAnalysis.top_egms.length === 0) && (
                      <tr>
                        <td colSpan={7} className="px-2 py-3 text-gray-500">No EGM-level gap for this day.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      {filters.trading_day && (
        <div className="mb-4 space-y-3">
          <div className="card !p-4 border border-indigo-100">
            <DailyTurnoverCharts
              charts={reconReport?.reconciliation?.charts}
              tradingDay={filters.trading_day}
              amount={reconReport?.reconciliation?.totals?.turnover}
              amountLoading={reconLoading}
              hasData={(reconReport?.reconciliation?.completeness?.distinct_egms || 0) > 0}
              partialDay={!!reconReport?.reconciliation?.completeness?.partial_day}
              allocateLabel={chartAllocateLabel}
              machineClass={filters.machine_class || 'all'}
              machineClassCounts={reconReport?.reconciliation?.charts?.machine_class_counts}
              onMachineClassChange={(mc) => setFilters((prev) => ({ ...prev, machine_class: mc }))}
              selectedEgmpks={chartSelectedEgmpks}
              selectedHours={chartSelectedHours}
              onToggleEgm={toggleChartEgm}
              onToggleHour={toggleChartHour}
              onClearFocus={resetChartFocus}
            />
          </div>
          <TurnoverFocusSessions
            tradingDay={filters.trading_day}
            machineClass={filters.machine_class || 'all'}
            selectedEgmpks={chartSelectedEgmpks}
            selectedHours={chartSelectedHours}
            allocateLabel={chartAllocateLabel}
          />
        </div>
      )}
      <div className="card">
        <div className="flex items-center justify-between mb-2">
          <div className="min-w-0">
            <h3 className="text-lg font-medium text-gray-900">
              {filters.view === 'summary'
                ? 'Combined Summary (Member × Trading Day)'
                : filters.view === 'sessions'
                  ? 'Combined (By Session — play timeline)'
                  : 'Combined (Attributed Intervals)'}
            </h3>
            {!loading && chartFocusActive && (
              <p className="text-xs text-amber-700 mt-1">
                Chart focus active
                {chartSelectedEgmpks.length ? ` · EGM ${chartSelectedEgmpks.join(', ')}` : ''}
                {chartSelectedHours.length ? ` · hour${chartSelectedHours.length > 1 ? 's' : ''} ${chartSelectedHours.join(', ')}` : ''}
                {' · '}
                <button type="button" className="underline font-medium" onClick={resetChartFocus}>Clear focus</button>
                {' '}or type House / machine ID to override.
              </p>
            )}
            {!loading && (
              <p className="text-xs text-gray-500 mt-0.5 tabular-nums">
                {filters.view === 'sessions'
                  ? (() => {
                      const n = displayedItems.length
                      const total = totalSessions != null ? totalSessions : n
                      const from = total === 0 ? 0 : (sessionPage - 1) * COMBINED_SESSION_PAGE_SIZE + 1
                      const to = total === 0 ? 0 : Math.min(sessionPage * COMBINED_SESSION_PAGE_SIZE, total)
                      const pageLine = total > COMBINED_SESSION_PAGE_SIZE
                        ? `Showing ${from}–${to} of ${total} sessions (page ${sessionPage} of ${sessionPageCount})`
                        : `${total} ${total === 1 ? 'session' : 'sessions'} listed`
                      return (
                        <>
                          {pageLine}
                          <span className="block mt-0.5 text-gray-400">
                            Enriched + attributed after Process &amp; Combine (meter, sessions, slot analysis, EGM). Carded and virtual uncarded sits when allocated.
                          </span>
                        </>
                      )
                    })()
                  : filters.view === 'summary'
                    ? `${displayedItems.length} member×day ${displayedItems.length === 1 ? 'row' : 'rows'} listed · expand a row for chronological sessions`
                    : (() => {
                        const n = displayedItems.length
                        const total = totalIntervals != null ? totalIntervals : n
                        const capped = total > n
                        return capped
                          ? `${n} of ${total} attributed interval${total === 1 ? '' : 's'} shown (chronological) — raise limit or narrow filters if truncated`
                          : `${total} attributed interval${total === 1 ? '' : 's'} listed (chronological)`
                      })()}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="btn-secondary"
              disabled={combineBusy || !filters.trading_day}
              title={!filters.trading_day ? 'Set Trading day first' : 'Delete enriched + attribution + rollups for this trading day'}
              onClick={async () => {
                if (!filters.trading_day) {
                  toast.error('Set Trading day first')
                  return
                }
                if (!window.confirm(`Clear combined/enriched/rollups for ${filters.trading_day}? Sessions and meters are kept.`)) return
                try {
                  setCombineBusy(true)
                  setCombineSummary(null)
                  setCombineProgress({ step: 1, total: 1, label: 'Clearing combined day…', pct: 50 })
                  const qs = new URLSearchParams({ trading_day: filters.trading_day, clear_rollups: 'true' })
                  const resp = await authFetch(getLocalApiUrl(`/api/gaming/combined/clear-day?${qs}`), { method: 'DELETE' })
                  const json = await resp.json().catch(() => ({}))
                  if (!resp.ok) throw new Error(json?.error || `Clear failed: ${resp.status}`)
                  setCombineProgress({ step: 1, total: 1, label: 'Clear complete', pct: 100 })
                  setCombineSummary(
                    `Cleared ${filters.trading_day}: attribution ${json.attribution_deleted ?? 0}, enriched ${json.enriched_deleted ?? 0}, virtual sessions ${json.virtual_sessions_deleted ?? 0}, member stats ${json.member_stats_deleted ?? 0}, txn rollup ${json.txn_rollup_deleted ?? 0}.`
                  )
                  toast.success(`Cleared combined data for ${filters.trading_day}`)
                  setItems([])
                  await load()
                  await loadPipelineStatus(true)
                  await loadDayTotals()
                  await loadReconciliation()
                } catch (err) {
                  toast.error(err?.message || 'Clear combined failed')
                } finally {
                  setCombineBusy(false)
                  setTimeout(() => setCombineProgress(null), 800)
                }
              }}
            >Clear combined day</button>
            <button
              className="btn-secondary"
              disabled={combineBusy}
              onClick={async()=>{
                const sleep = (ms) => new Promise((r) => setTimeout(r, ms))
                try {
                  setCombineBusy(true)
                  setCombineSummary(null)
                  const day = (filters.trading_day || '').trim() || (filters.from || '').slice(0, 10) || undefined
                  if (!day && !filters.from && !filters.to) {
                    toast.error('Set Trading day (or From/To) before Process & Combine')
                    return
                  }
                  const body = {
                    egm_pk: filters.egm_pk || undefined,
                    member_id: filters.member_id || undefined,
                    trading_day: day,
                    from: day ? undefined : (filters.from || undefined),
                    to: day ? undefined : (filters.to || undefined),
                    clear_before: true,
                  }

                  setCombineProgress({
                    step: 1,
                    total: 5,
                    label: 'Starting background combine…',
                    pct: 5,
                    detail: 'API stays responsive — job runs in a separate process',
                  })

                  const startResp = await authFetch(getLocalApiUrl('/api/gaming/process/combine'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                  })
                  const startJson = await startResp.json().catch(() => ({}))
                  if (startResp.status === 409 && startJson.job_id) {
                    toast.info(`Combine already running (job ${startJson.job_id}) — showing progress`)
                  } else if (!startResp.ok) {
                    throw new Error(startJson?.error || `Combine start failed: ${startResp.status}`)
                  }
                  const jobId = startJson.job_id
                  if (!jobId) throw new Error('Combine did not return a job id')

                  let finalJob = null
                  for (let i = 0; i < 720; i += 1) {
                    const stResp = await authFetch(getLocalApiUrl(`/api/gaming/process/combine/${jobId}`))
                    const stJson = await stResp.json().catch(() => ({}))
                    if (!stResp.ok) throw new Error(stJson?.error || `Status poll failed: ${stResp.status}`)
                    const d = stJson.data || {}
                    const stats = d.stats || {}
                    const uiStep = combineUiStepFromPoll(d)
                    const pct = combinePctFromPoll(d, uiStep)
                    const detailParts = []
                    const elapsedLabel = formatElapsedMs(d.elapsed_ms ?? stats.elapsed_ms)
                    if (elapsedLabel) detailParts.push(`Elapsed ${elapsedLabel}`)
                    if (stats.enriched_count != null) detailParts.push(`Enriched ${stats.enriched_count}`)
                    if (stats.attribution_count != null) detailParts.push(`Attribution ${stats.attribution_count}`)
                    if (stats.enrich?.sessions_enriched != null) {
                      detailParts.push(`${stats.enrich.sessions_enriched} sessions enriched`)
                    }
                    if (stats.allocate?.meter_intervals != null) {
                      detailParts.push(`${stats.allocate.intervals_with_session_overlap ?? '—'}/${stats.allocate.meter_intervals} intervals matched`)
                    }
                    const v = stats.virtual
                    if (v?.phase === 'virtual') {
                      if (v.step === 'sessions' && v.total_sits != null) {
                        detailParts.push(`Virtual sits ${v.done_sits ?? 0}/${v.total_sits}`)
                      } else if (v.step === 'attribution' && v.total_attr != null) {
                        detailParts.push(`Virtual attr ${v.done_attr ?? 0}/${v.total_attr}`)
                      } else if (v.step === 'enrich' && v.total_sits != null) {
                        detailParts.push(`Virtual enrich ${v.done_sits ?? 0}/${v.total_sits}`)
                      }
                    }
                    setCombineProgress({
                      step: uiStep,
                      total: COMBINE_UI_PHASES.length,
                      label: d.phase_label || d.phase || 'Processing…',
                      pct,
                      detail: detailParts.join(' · ') || `Job ${jobId} · ${d.status || 'running'}`,
                      jobId,
                    })
                    if (d.status === 'success') {
                      finalJob = d
                      break
                    }
                    if (d.status === 'failed') {
                      throw new Error(d.error_message || 'Combine failed')
                    }
                    await loadDayTotals()
                    await loadPipelineStatus()
                    await sleep(2500)
                  }
                  if (!finalJob) throw new Error('Combine timed out waiting for completion')

                  const stats = finalJob.stats || {}
                  const e = stats.enrich || {}
                  const a = stats.allocate || {}
                  const c = stats.cleared
                  const rollupJson = stats.rollup
                  const elapsedLabel = formatElapsedMs(finalJob.elapsed_ms ?? stats.elapsed_ms)
                  const summaryLines = [
                    elapsedLabel ? `Processing time: ${elapsedLabel} (job ${jobId}).` : null,
                    c ? `Cleared: attribution ${c.attribution_deleted ?? 0}, enriched ${c.enriched_deleted ?? 0}, virtual ${c.virtual_sessions_deleted ?? 0}, rollups ${c.txn_rollup_deleted ?? 0}/${c.member_stats_deleted ?? 0}.` : null,
                    `Enrich: ${e.sessions_considered ?? '—'} session(s) considered, ${e.sessions_enriched ?? '—'} enriched.`,
                    `Allocate: ${a.meter_intervals ?? '—'} meter intervals; ${a.intervals_with_session_overlap ?? '—'} overlapped sessions; ${stats.attribution_count ?? '—'} attribution rows.`,
                    rollupJson ? `Rollup: processed ${rollupJson.processed ?? '—'}, upserted ${rollupJson.upserted ?? '—'}.` : null,
                  ].filter(Boolean)
                  setCombineSummary(summaryLines.join('\n\n'))
                  setEmptyHint(null)
                  toast.success(
                    elapsedLabel
                      ? `Combine done in ${elapsedLabel} (job ${jobId}).`
                      : `Combine done (job ${jobId}).`,
                    { duration: 10000 }
                  )
                  setCombineProgress({
                    step: COMBINE_UI_PHASES.length,
                    total: COMBINE_UI_PHASES.length,
                    label: elapsedLabel ? `Complete in ${elapsedLabel}` : 'Complete',
                    pct: 100,
                    detail: elapsedLabel ? `Total processing time: ${elapsedLabel}` : null,
                    jobId,
                  })
                  await load()
                  await loadPipelineStatus()
                  await loadDayTotals()
                  await loadReconciliation()
                } catch (err) {
                  toast.error(err?.message || 'Process & Combine failed')
                  setCombineProgress((prev) => (prev ? { ...prev, label: `Failed: ${err?.message || 'error'}`, error: true } : null))
                } finally {
                  setCombineBusy(false)
                  setTimeout(() => setCombineProgress(null), 8000)
                }
              }}
            >{combineBusy ? 'Processing…' : 'Process & Combine'}</button>
            <div className="text-sm text-gray-500">{loading && !combineBusy ? 'Loading…' : ''}</div>
          </div>
        </div>
        {combineProgress && (
          <div className={`mb-3 rounded-lg border px-3 py-2.5 ${combineProgress.error ? 'border-red-200 bg-red-50' : 'border-teal-200 bg-teal-50'}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
              <div className={`text-sm font-medium ${combineProgress.error ? 'text-red-800' : 'text-teal-900'}`}>
                {combineProgress.label}
              </div>
              <div className={`text-xs tabular-nums ${combineProgress.error ? 'text-red-700' : 'text-teal-700'}`}>
                Step {combineProgress.step}/{combineProgress.total} · {combineProgress.pct}%
              </div>
            </div>
            <div className="h-2 w-full rounded-full bg-white/80 overflow-hidden border border-teal-100">
              <div
                className={`h-full rounded-full transition-all duration-300 ${combineProgress.error ? 'bg-red-500' : 'bg-teal-600'}`}
                style={{ width: `${Math.max(4, Math.min(100, combineProgress.pct || 0))}%` }}
              />
            </div>
            {combineProgress.detail && (
              <div className={`mt-1.5 text-xs tabular-nums ${combineProgress.error ? 'text-red-700' : 'text-teal-800'}`}>
                {combineProgress.detail}
              </div>
            )}
            <div className="mt-1.5 flex flex-wrap gap-1.5 text-[10px] uppercase tracking-wide">
              {['Clear', 'Enrich', 'Allocate', 'Rollup', 'Reload'].map((name, i) => {
                const done = combineProgress.pct >= 100 && !combineProgress.error
                  ? true
                  : combineProgress.step > i + 1
                const active = combineProgress.step === i + 1 && combineBusy
                return (
                  <span
                    key={name}
                    className={`rounded px-1.5 py-0.5 ${
                      done ? 'bg-teal-600 text-white'
                        : active ? 'bg-teal-200 text-teal-900'
                          : 'bg-white/70 text-gray-500'
                    }`}
                  >
                    {name}
                  </span>
                )
              })}
            </div>
          </div>
        )}
        {error && <div className="text-sm text-red-600 mb-2">{error}</div>}
        {combineSummary && !error && (
          <div className="text-sm text-gray-800 bg-slate-50 border border-slate-200 rounded px-3 py-2 mb-2 whitespace-pre-wrap">
            {combineSummary.split('\n\n').map((line, idx) => (
              idx === 0 && /^Processing time:/.test(line)
                ? <div key={idx} className="font-medium text-teal-900 mb-2">{line}</div>
                : <div key={idx} className={idx > 0 ? 'mt-2' : ''}>{line}</div>
            ))}
          </div>
        )}
        {(emptyHint || bySessionEmptyMessage) && !error && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded px-3 py-2 mb-2">
            {bySessionEmptyMessage || emptyHint}
            {bySessionFiltersActive.length > 0 && (
              <button type="button" className="ml-2 underline font-semibold" onClick={clearBySessionFilters}>
                Clear session filters
              </button>
            )}
          </div>
        )}
        <div className="overflow-x-auto">
          {filters.view==='summary' ? (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left w-8"></th>
                  <th className="px-3 py-2 text-left">TradingDay</th>
                  <th className="px-3 py-2 text-left">Player ID</th>
                  <th className="px-3 py-2 text-left">Sessions</th>
                  <th className="px-3 py-2 text-left">EGMs</th>
                  <th className="px-3 py-2 text-left">Turnover</th>
                  <th className="px-3 py-2 text-left">CreditWin</th>
                  {displayedItems?.[0]?.Turnover_credits !== undefined && (
                    <>
                      <th className="px-3 py-2 text-left">Turnover (credits)</th>
                      <th className="px-3 py-2 text-left">Turnover (converted)</th>
                    </>
                  )}
                  <th className="px-3 py-2 text-left">CashIn</th>
                  <th className="px-3 py-2 text-left">CashlessIn</th>
                  <th className="px-3 py-2 text-left">Bills (5/10/20/50/100)</th>
                  <th className="px-3 py-2 text-left">TicketsIn</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {displayedItems.map((r, idx) => {
                  const memberId = r.MemberID ?? r.memberid
                  const tradingDay = memberDayTradingDayParam(r, filters.trading_day)
                  const cacheKey = memberDayCacheKey(memberId, tradingDay)
                  const memberOpen = expandedMembers.has(cacheKey)
                  const memberCache = memberSessionCache[cacheKey]
                  const summaryColSpan = displayedItems?.[0]?.Turnover_credits !== undefined ? 13 : 11
                  return (
                    <React.Fragment key={`${memberId}-${tradingDay}-${idx}`}>
                      <tr
                        className={`cursor-pointer hover:bg-slate-50 ${memberOpen ? 'bg-slate-50' : ''}`}
                        onClick={() => toggleMemberExpand(r)}
                      >
                        <td className="px-3 py-2 text-gray-500">{memberOpen ? '▾' : '▸'}</td>
                        <td className="px-3 py-2">{(() => { const d = parseDate(r.TradingDay); return d ? formatSydneyDate(d) : '—' })()}</td>
                        <td className="px-3 py-2">{memberId}</td>
                        <td className="px-3 py-2">{r.sessions_count}</td>
                        <td className="px-3 py-2">{r.egm_count}</td>
                        <td className="px-3 py-2 text-right">{formatAllocField('Turnover_alloc', r.Turnover_alloc)}</td>
                        <td className="px-3 py-2 text-right">{formatAllocField('CreditWin_alloc', r.CreditWin_alloc)}</td>
                        {displayedItems?.[0]?.Turnover_credits !== undefined && (
                          <>
                            <td className="px-3 py-2 text-right">{formatAllocField('Turnover_credits', r.Turnover_credits)}</td>
                            <td className="px-3 py-2 text-right">{formatAllocField('Turnover_converted', r.Turnover_converted)}</td>
                          </>
                        )}
                        <td className="px-3 py-2 text-right">{formatAllocField('CashIn_alloc', r.CashIn_alloc)}</td>
                        <td className="px-3 py-2 text-right">{formatAllocField('CashlessIn_alloc', r.CashlessIn_alloc)}</td>
                        <td className="px-3 py-2">{[r.Bill5_alloc, r.Bill10_alloc, r.Bill20_alloc, r.Bill50_alloc, r.Bill100_alloc].map((x) => x ?? 0).join('/')}</td>
                        <td className="px-3 py-2 text-right">{formatAllocField('TicketsIn_alloc', r.TicketsIn_alloc)}</td>
                      </tr>
                      {memberOpen && (
                        <tr className="bg-slate-50/80">
                          <td colSpan={summaryColSpan} className="px-4 py-3">
                            {memberCache?.loading ? (
                              <p className="text-sm text-gray-500">Loading sessions…</p>
                            ) : memberCache?.error ? (
                              <p className="text-sm text-red-600">{memberCache.error}</p>
                            ) : (memberCache?.sessions || []).length === 0 ? (
                              <p className="text-sm text-gray-500">No attributed sessions for this member on this trading day.</p>
                            ) : (
                              <div>
                                <p className="text-xs text-gray-600 mb-2">
                                  Chronological play for member {memberId} · cumulative totals across the day · click a session for interval detail
                                </p>
                                <div className="overflow-x-auto rounded border border-slate-200 bg-white">
                                  <table className="min-w-full text-xs">
                                    <thead className="bg-gray-50 text-gray-600">
                                      <tr>
                                        <th className="px-2 py-1.5 text-left w-6"></th>
                                        <th className="px-2 py-1.5 text-left">Session start</th>
                                        <th className="px-2 py-1.5 text-left">House</th>
                                        <th className="px-2 py-1.5 text-left">Machine ID</th>
                                        <th className="px-2 py-1.5 text-left">Duration</th>
                                        <th className="px-2 py-1.5 text-right">Turnover</th>
                                        <th className="px-2 py-1.5 text-right">CreditWin</th>
                                        <th className="px-2 py-1.5 text-right">Net</th>
                                        <th className="px-2 py-1.5 text-right">Cum TO</th>
                                        <th className="px-2 py-1.5 text-right">Cum Win</th>
                                        <th className="px-2 py-1.5 text-right">Cum Net</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                      {(memberCache?.sessions || []).map((s) => {
                                        const spk = s.SessionPK ?? s.sessionpk
                                        const sessionOpen = expandedSessions.has(spk)
                                        const start = parseDate(s.SessionStart || s.FirstInterval || s.sessionstart || s.firstinterval)
                                        const end = parseDate(s.SessionEnd || s.LastInterval || s.sessionend || s.lastinterval)
                                        const mins = start && end
                                          ? Math.max(0, Math.round((end - start) / 60000))
                                          : (s.EnrichedSessionMinutes != null || s.enrichedsessionminutes != null
                                            ? Number(s.EnrichedSessionMinutes ?? s.enrichedsessionminutes)
                                            : null)
                                        const turnover = allocNum(s, 'Turnover_alloc', 'turnover_alloc')
                                        const win = allocNum(s, 'CreditWin_alloc', 'creditwin_alloc')
                                        const net = turnover - win
                                        return (
                                          <React.Fragment key={spk}>
                                            <tr
                                              className={`cursor-pointer hover:bg-slate-50 ${sessionOpen ? 'bg-slate-50' : ''}`}
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                toggleSessionExpand(spk)
                                              }}
                                            >
                                              <td className="px-2 py-1.5 text-gray-500">{sessionOpen ? '▾' : '▸'}</td>
                                              <td className="px-2 py-1.5 whitespace-nowrap">{start ? formatSydneyDateTime(start) : '—'}</td>
                                              <td className="px-2 py-1.5 font-mono">{combinedEgmHouse(s)}</td>
                                              <td className="px-2 py-1.5 font-mono text-gray-600">{combinedEgmMachineId(s)}</td>
                                              <td className="px-2 py-1.5">{mins != null ? `${mins}m` : '—'}</td>
                                              <td className="px-2 py-1.5 text-right">{formatAllocField('Turnover_alloc', turnover)}</td>
                                              <td className="px-2 py-1.5 text-right">{formatAllocField('CreditWin_alloc', win)}</td>
                                              <td className={`px-2 py-1.5 text-right font-medium ${net >= 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                                                {formatAllocField('Turnover_alloc', net)}
                                              </td>
                                              <td className="px-2 py-1.5 text-right">{formatAllocField('Turnover_alloc', s.MemberDayCumTurnover)}</td>
                                              <td className="px-2 py-1.5 text-right">{formatAllocField('CreditWin_alloc', s.MemberDayCumCreditWin)}</td>
                                              <td className={`px-2 py-1.5 text-right font-medium ${(s.MemberDayCumNet ?? 0) >= 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                                                {formatAllocField('Turnover_alloc', s.MemberDayCumNet)}
                                              </td>
                                            </tr>
                                            {sessionOpen && (
                                              <tr>
                                                <td colSpan={11} className="px-3 py-2 bg-white">
                                                  <CombinedSessionIntervalTable session={s} sessionPk={spk} parseDate={parseDate} />
                                                </td>
                                              </tr>
                                            )}
                                          </React.Fragment>
                                        )
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
                {displayedItems.length === 0 && !loading && (
                  <tr><td className="px-3 py-4 text-gray-500" colSpan={displayedItems?.[0]?.Turnover_credits !== undefined ? 13 : 11}>
                    No summary data. Click <strong>Process & Combine</strong> above to enrich sessions with meter readings.
                  </td></tr>
                )}
              </tbody>
            </table>
          ) : filters.view === 'sessions' ? (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left w-8"></th>
                  <th className="px-3 py-2 text-left">Session start</th>
                  <th className="px-3 py-2 text-left" title="Floor house number">House</th>
                  <th className="px-3 py-2 text-left" title="DimMachine machine pk / vendor id">Machine ID</th>
                  <th className="px-3 py-2 text-left">SessionPK</th>
                  <th className="px-3 py-2 text-left">Player</th>
                  <th className="px-3 py-2 text-left">Intervals</th>
                  <th className="px-3 py-2 text-left">Duration</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap" title="Games/handle count from meter stroke_alloc (or session stroke). Used with turnover to infer avg bet.">Stroke</th>
                  <th className="px-3 py-2 text-left">Turnover</th>
                  <th className="px-3 py-2 text-left">CreditWin</th>
                  <th className="px-3 py-2 text-left" title="Cancel credit + cashless/coin out (meter handpay / ticket payout path)">Payout</th>
                  <th className="px-3 py-2 text-left" title="Session jackpot is from PlayerSession. EGM-day is the machine’s Slot Analysis total for the trading day — shared by every sit on that EGM, not this session’s win.">Jackpot</th>
                  <th className="px-3 py-2 text-left">Cash / Cashless / Tickets</th>
                  <th className="px-3 py-2 text-left">Net (TO−Win)</th>
                  <th className="px-3 py-2 text-left">Play shape</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {displayedItems.map((r) => {
                  const spk = r.SessionPK ?? r.sessionpk
                  const open = expandedSessions.has(spk)
                  const isUnc = r.IsUncarded === true || r.IsUncarded === 't' || r.MemberID === '__UNCARDED__' || String(spk || '').startsWith('UNC_')
                  const turnover = allocNum(r, 'Turnover_alloc', 'turnover_alloc')
                  const win = allocNum(r, 'CreditWin_alloc', 'creditwin_alloc')
                  const cashIn = allocNum(r, 'CashIn_alloc', 'cashin_alloc') + allocNum(r, 'CashDrop_alloc', 'cashdrop_alloc')
                  const cashless = allocNum(r, 'CashlessIn_alloc', 'cashlessin_alloc')
                  const tickets = allocNum(r, 'TicketsIn_alloc', 'ticketsin_alloc')
                  const payoutCents = allocPayoutCents(r)
                  const start = parseDate(r.SessionStart || r.FirstInterval || r.sessionstart || r.firstinterval)
                  const end = parseDate(r.SessionEnd || r.LastInterval || r.sessionend || r.lastinterval)
                  const mins = start && end ? Math.max(0, Math.round((end - start) / 60000)) : (r.EnrichedSessionMinutes != null || r.enrichedsessionminutes != null ? Number(r.EnrichedSessionMinutes ?? r.enrichedsessionminutes) : null)
                  const ivs = Array.isArray(r.intervals) ? r.intervals : []
                  const intervalCount = Number(r.IntervalCount ?? r.intervalcount)
                  const intervalDisplay = Number.isFinite(intervalCount) && intervalCount > 0 ? intervalCount : (ivs.length || intervalCount || 0)
                  const maxTo = Math.max(1, ...ivs.map((iv) => allocNum(iv, 'Turnover_alloc', 'turnover_alloc')))
                  const maxPayout = Math.max(1, ...ivs.map((iv) => allocPayoutCents(iv)))
                  const sessJp = Number(r.EnrichedSessionJackpot ?? r.enrichedsessionjackpot ?? 0) || 0
                  const dayJp = Number(r.EnrichedDailyJackpot ?? r.enricheddailyjackpot ?? 0) || 0
                  const dayProg = Number(r.EnrichedDailyProgressive ?? r.enricheddailyprogressive ?? 0) || 0
                  const hasJp = enrichFlagYes(r.EnrichedHasJackpot ?? r.enrichedhasjackpot) || sessJp > 0 || dayJp > 0 || dayProg > 0
                  const strokeAlloc = allocNum(r, 'Stroke_alloc', 'stroke_alloc')
                  const strokeEnrich = Number(r.EnrichedMeterStroke ?? r.enrichedmeterstroke ?? r.EnrichedSessionStroke ?? r.enrichedsessionstroke ?? 0) || 0
                  const strokeSession = Number(r.SessionStroke ?? r.sessionstroke ?? 0) || 0
                  // Prefer allocated meter stroke; enrich is carded-only; SessionStroke covers virtual uncarded.
                  const strokeShow = strokeAlloc > 0 ? strokeAlloc : (strokeEnrich > 0 ? strokeEnrich : strokeSession)
                  const avgBetRaw = Number(r.EnrichedAvgBet ?? r.enrichedavgbet)
                  const avgBet = Number.isFinite(avgBetRaw) && avgBetRaw > 0
                    ? avgBetRaw
                    : (strokeShow > 0 && turnover > 0 ? (turnover / 100) / strokeShow : NaN)
                  return (
                    <React.Fragment key={spk}>
                      <tr
                        className={`cursor-pointer hover:bg-slate-50 ${open ? 'bg-slate-50' : ''}`}
                        onClick={() => toggleSessionExpand(spk)}
                      >
                        <td className="px-3 py-2 text-gray-500">{open ? '▾' : '▸'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{start ? formatSydneyDateTime(start) : '—'}</td>
                        <td className="px-3 py-2 font-mono">{combinedEgmHouse(r)}</td>
                        <td className="px-3 py-2 font-mono text-gray-600">{combinedEgmMachineId(r)}</td>
                        <td className="px-3 py-2 font-mono text-xs max-w-[14rem] truncate" title={spk}>{spk}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${isUnc ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                            {isUnc ? 'Uncarded' : (r.MemberID || r.memberid || 'Carded')}
                          </span>
                        </td>
                        <td className="px-3 py-2">{intervalDisplay}</td>
                        <td className="px-3 py-2">{mins != null ? `${mins}m` : '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums" title={Number.isFinite(avgBet) && avgBet > 0 ? `Avg bet ≈ ${formatGamingMoney(avgBet)}` : 'Stroke / games'}>
                          {strokeShow > 0 ? Math.round(strokeShow).toLocaleString() : '—'}
                        </td>
                        <td className="px-3 py-2 text-right">{formatAllocField('Turnover_alloc', turnover)}</td>
                        <td className="px-3 py-2 text-right">{formatAllocField('CreditWin_alloc', win)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums ${payoutCents > 0 ? 'text-rose-700 font-medium' : ''}`} title="Cancel credit + cashless/coin out">
                          {formatPayoutAlloc(r)}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {hasJp ? (
                            sessJp > 0 ? (
                              <span
                                className="inline-flex flex-col gap-0.5"
                                title="Jackpot attributed to this session (FactPlayerSession ExternalWin / Jackpots)"
                              >
                                <span className="inline-flex w-fit items-center rounded px-1.5 py-0.5 font-medium bg-violet-100 text-violet-800">Session JP</span>
                                <span className="text-violet-900 tabular-nums font-medium">{formatGamingMoney(sessJp)}</span>
                              </span>
                            ) : (dayJp > 0 || dayProg > 0) ? (
                              <span
                                className="inline-flex flex-col gap-0.5"
                                title={[
                                  dayJp > 0 ? `EGM-day jackpot (Slot Analysis): $${dayJp.toFixed(2)}` : null,
                                  dayProg > 0 ? `EGM-day progressive (Slot Analysis): $${dayProg.toFixed(2)}` : null,
                                  'Machine total for the trading day — same figure on every sit on this EGM. Not this session’s win.',
                                ].filter(Boolean).join(' · ')}
                              >
                                <span className="inline-flex w-fit items-center rounded px-1.5 py-0.5 font-medium bg-slate-100 text-slate-700">EGM-day</span>
                                <span className="text-slate-700 tabular-nums">
                                  {formatGamingMoney(dayJp > 0 ? dayJp : dayProg)}
                                </span>
                                <span className="text-[10px] text-slate-500 leading-tight">not this sit</span>
                              </span>
                            ) : (
                              <span className="inline-flex w-fit items-center rounded px-1.5 py-0.5 font-medium bg-violet-100 text-violet-800">JP</span>
                            )
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-right whitespace-nowrap" title={cashInCalculatedHint(r, cashIn) || undefined}>
                          {formatCashInWithBills(r, cashIn)} / {formatAllocField('CashlessIn_alloc', cashless)} / {formatAllocField('TicketsIn_alloc', tickets)}
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${turnover - win >= 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                          {formatAllocField('Turnover_alloc', turnover - win)}
                        </td>
                        <td className="px-3 py-2 min-w-[7rem]">
                          {ivs.length > 0 ? (
                            <div className="flex items-end gap-px h-8" title="Turnover (grey/amber) and payout (rose) per interval — oldest → newest">
                              {ivs.map((iv, i) => {
                                const toCents = allocNum(iv, 'Turnover_alloc', 'turnover_alloc')
                                const payCents = allocPayoutCents(iv)
                                const h = Math.max(2, Math.round((toCents / maxTo) * 28))
                                const payH = payCents > 0 ? Math.max(2, Math.round((payCents / maxPayout) * 28)) : 0
                                const hasCash = (allocNum(iv, 'CashIn_alloc', 'cashin_alloc') + allocNum(iv, 'CashDrop_alloc', 'cashdrop_alloc') + allocNum(iv, 'CashlessIn_alloc', 'cashlessin_alloc') + allocNum(iv, 'TicketsIn_alloc', 'ticketsin_alloc')) > 0
                                return (
                                  <div key={i} className="flex flex-col justify-end flex-1 min-w-[3px] gap-px h-8">
                                    {payH > 0 ? (
                                      <div className="rounded-sm bg-rose-500 w-full" style={{ height: `${payH}px` }} title={`Payout ${formatMeterCents(payCents)}`} />
                                    ) : null}
                                    <div
                                      className={`rounded-sm w-full ${hasCash ? 'bg-amber-500' : 'bg-slate-400'}`}
                                      style={{ height: `${h}px` }}
                                    />
                                  </div>
                                )
                              })}
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                      {open && (
                        <tr className="bg-slate-50/80">
                          <td colSpan={16} className="px-4 py-3">
                            <div className="text-xs text-gray-600 mb-2">
                              Play timeline · cumulative turnover vs wins · payout = cancel credit + cashless/coin out
                              {' · '}
                              <span className="text-gray-500">CashIn (Calculated) = overlap-prorated / rounded — not a direct meter bill read</span>
                              {` · Stroke ${strokeShow > 0 ? Math.round(strokeShow).toLocaleString() : '—'}`}
                              {Number.isFinite(avgBet) && avgBet > 0 ? ` · Avg bet ≈ ${formatGamingMoney(avgBet)}` : ''}
                              {r.EnrichedPatronRiskLevel ? ` · Risk ${r.EnrichedPatronRiskLevel}` : ''}
                              {enrichFlagYes(r.EnrichedRapidPlay) ? ' · Rapid play' : ''}
                              {enrichFlagYes(r.EnrichedExtendedPlay) ? ' · Extended play' : ''}
                              {hasJp ? (
                                <span className={sessJp > 0 ? 'text-violet-800 font-medium' : 'text-slate-600 font-medium'}>
                                  {sessJp > 0
                                    ? ` · Session jackpot $${sessJp.toFixed(2)}`
                                    : ` · EGM-day jackpot hint${dayJp > 0 ? ` $${dayJp.toFixed(2)}` : ''}${dayProg > 0 ? ` · progressive $${dayProg.toFixed(2)}` : ''} (not attributed to this sit)`}
                                </span>
                              ) : null}
                            </div>
                            {ivs.length === 0 ? (
                              <p className="text-sm text-gray-500">No interval detail returned for this session.</p>
                            ) : (
                              <CombinedSessionIntervalTable session={r} sessionPk={spk} parseDate={parseDate} />
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
                {displayedItems.length === 0 && !loading && (
                  <tr>
                    <td className="px-3 py-4 text-gray-500" colSpan={15}>
                      {bySessionEmptyMessage || emptyHint ? (
                        bySessionEmptyMessage || emptyHint
                      ) : filters.machine_class && filters.machine_class !== 'all' && items.length === 0 ? (
                        'No rows for this Machine type. If Type is unset in EGM Registry, go to Group Configuration → EGMs → Populate types from DimMachine, then reload.'
                      ) : items.length > 0 ? (
                        'No sessions match the current filters (cash in / min turnover / net / min net gain). Clear or lower a filter and reload.'
                      ) : (
                        <>No attributed sessions. Meter and Sessions tabs show raw data only — click <strong>Process &amp; Combine</strong> here after import.</>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">IntervalStart</th>
                  <th className="px-3 py-2 text-left">House</th>
                  <th className="px-3 py-2 text-left">Machine ID</th>
                  <th className="px-3 py-2 text-left">SessionPK</th>
                  <th className="px-3 py-2 text-left">Player ID</th>
                  <th className="px-3 py-2 text-left" title="Carded: session time in this meter interval ÷ interval length. Uncarded residual rows show —.">Time share</th>
                  <th className="px-3 py-2 text-left">Turnover_alloc</th>
                  <th className="px-3 py-2 text-left">CreditWin_alloc</th>
                  <th className="px-3 py-2 text-left">CashIn_alloc</th>
                  <th className="px-3 py-2 text-left">CashlessIn_alloc</th>
                  <th className="px-3 py-2 text-left">TicketsIn_alloc</th>
                  <th className="px-3 py-2 text-left border-l border-gray-200" title="From gaming_session_enriched after Process &amp; Combine">Risk lvl</th>
                  <th className="px-3 py-2 text-left" title="Patron overall risk score">Risk #</th>
                  <th className="px-3 py-2 text-left">Tier</th>
                  <th className="px-3 py-2 text-left" title="Session duration (minutes)">Sess min</th>
                  <th className="px-3 py-2 text-left" title="Full-session turnover in currency (enriched)">Sess TO</th>
                  <th className="px-3 py-2 text-left" title="Rapid play flag">Rpd</th>
                  <th className="px-3 py-2 text-left" title="Extended play flag">Ext</th>
                  <th className="px-3 py-2 text-left">PEP</th>
                  <th className="px-3 py-2 text-left">Snc</th>
                  <th className="px-3 py-2 text-left">Case</th>
                  <th className="px-3 py-2 text-left text-xs" title="enriched_at">Enr at</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {displayedItems.map((r)=> (
                  <tr key={`${r.EgmPK ?? r.egmpk}-${combinedIntervalStart(r)}-${r.SessionPK ?? r.sessionpk}`}>
                    <td className="px-3 py-2">{(()=>{ const d=parseDate(combinedIntervalStart(r)); return d? formatSydneyDateTime(d) : '—'})()}</td>
                    <td className="px-3 py-2 font-mono">{combinedEgmHouse(r)}</td>
                    <td className="px-3 py-2 font-mono text-gray-600">{combinedEgmMachineId(r)}</td>
                    <td className="px-3 py-2 font-mono">{r.SessionPK ?? r.sessionpk}</td>
                    <td className="px-3 py-2">{r.MemberID ?? r.memberid}</td>
                    <td className="px-3 py-2" title={formatCombinedOverlapPct(r).title}>{formatCombinedOverlapPct(r).text}</td>
                    <td className="px-3 py-2 text-right">{formatAllocField('Turnover_alloc', r.Turnover_alloc ?? r.turnover_alloc)}</td>
                    <td className="px-3 py-2 text-right">{formatAllocField('CreditWin_alloc', r.CreditWin_alloc ?? r.creditwin_alloc)}</td>
                    <td className="px-3 py-2 text-right" title={cashInCalculatedHint(r, (Number(r.CashIn_alloc ?? r.cashin_alloc) || 0) + (Number(r.CashDrop_alloc ?? r.cashdrop_alloc) || 0)) || undefined}>
                      {formatCashInWithBills(r, (Number(r.CashIn_alloc ?? r.cashin_alloc) || 0) + (Number(r.CashDrop_alloc ?? r.cashdrop_alloc) || 0))}
                    </td>
                    <td className="px-3 py-2 text-right">{formatAllocField('CashlessIn_alloc', r.CashlessIn_alloc ?? r.cashlessin_alloc)}</td>
                    <td className="px-3 py-2 text-right">{formatAllocField('TicketsIn_alloc', r.TicketsIn_alloc ?? r.ticketsin_alloc)}</td>
                    <td className="px-3 py-2 border-l border-gray-200 text-xs">{r.EnrichedPatronRiskLevel ?? r.enrichedpatronrisklevel ?? '—'}</td>
                    <td className="px-3 py-2 text-xs">{r.EnrichedPatronRiskScore != null || r.enrichedpatronriskscore != null ? Number(r.EnrichedPatronRiskScore ?? r.enrichedpatronriskscore).toFixed(0) : '—'}</td>
                    <td className="px-3 py-2 text-xs">{r.EnrichedMemberTier ?? r.enrichedmembertier ?? '—'}</td>
                    <td className="px-3 py-2 text-xs">{r.EnrichedSessionMinutes != null || r.enrichedsessionminutes != null ? Number(r.EnrichedSessionMinutes ?? r.enrichedsessionminutes).toFixed(0) : '—'}</td>
                    <td className="px-3 py-2 text-xs text-right">{r.EnrichedSessionTurnover != null || r.enrichedsessionturnover != null ? formatGamingMoney(r.EnrichedSessionTurnover ?? r.enrichedsessionturnover) : '—'}</td>
                    <td className="px-3 py-2 text-xs">{enrichFlagYes(r.EnrichedRapidPlay ?? r.enrichedrapidplay) ? 'Y' : '—'}</td>
                    <td className="px-3 py-2 text-xs">{enrichFlagYes(r.EnrichedExtendedPlay ?? r.enrichedextendedplay) ? 'Y' : '—'}</td>
                    <td className="px-3 py-2 text-xs">{enrichFlagYes(r.EnrichedHasPep ?? r.enrichedhaspep) ? 'Y' : '—'}</td>
                    <td className="px-3 py-2 text-xs">{enrichFlagYes(r.EnrichedHasSanctions ?? r.enrichedhassanctions) ? 'Y' : '—'}</td>
                    <td className="px-3 py-2 text-xs">{enrichFlagYes(r.EnrichedHasActiveCase ?? r.enrichedhasactivecase) ? 'Y' : '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{(() => { const t = r.EnrichedAt ?? r.enrichedat; if (!t) return '—'; try { return formatSydneyDateTime(t) } catch { return '—' } })()}</td>
                  </tr>
                ))}
                {displayedItems.length === 0 && !loading && (
                  <tr><td className="px-3 py-4 text-gray-500" colSpan={22}>
                    {emptyHint
                      ? emptyHint
                      : filters.machine_class && filters.machine_class !== 'all' && items.length === 0
                      ? 'No rows for this Machine type. If Type is unset in EGM Registry, go to Group Configuration → EGMs → Populate types from DimMachine, then reload.'
                      : chartFocusActive && items.length === 0
                        ? <>Chart focus is filtering all intervals. <button type="button" className="underline font-medium" onClick={resetChartFocus}>Clear focus</button> and reload.</>
                      : <>No combined data. Click <strong>Process & Combine</strong> above to enrich sessions with meter readings and allocate intervals.</>}
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        {filters.view === 'sessions' && totalSessions != null && totalSessions > COMBINED_SESSION_PAGE_SIZE && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            <button
              type="button"
              className="btn-secondary"
              disabled={loading || sessionPage <= 1}
              onClick={() => setSessionPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span className="text-gray-500 tabular-nums">
              Page {sessionPage} of {sessionPageCount}
            </span>
            <button
              type="button"
              className="btn-secondary"
              disabled={loading || sessionPage >= sessionPageCount}
              onClick={() => setSessionPage((p) => Math.min(sessionPageCount, p + 1))}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function SummaryTab({ initialFilters }) {
  const [items, setItems] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [filters, setFilters] = React.useState(() => ({
    member_id: initialFilters?.member_id || '',
    from: initialFilters?.from || '',
    to: initialFilters?.to || '',
  }))
  const [processing, setProcessing] = React.useState(false)

  const load = React.useCallback(async ()=>{
    try {
      setLoading(true); setError(null)
      const qs = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([,v])=>v)))
      const r = await authFetch(getLocalApiUrl(`/api/gaming/sessions/stats/daily?${qs}`))
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed to load daily stats')
      setItems(j.data || [])
    } catch (e) { setError(e.message) } finally { setLoading(false) }
  }, [filters])

  React.useEffect(()=>{ load() }, [load])

  return (
    <div>
      <div className="card mb-4">
        <div className="grid grid-cols-6 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">{GAMING_MEMBER_FILTER_LABEL}</label>
            <input className="input-field w-full" placeholder={GAMING_MEMBER_FILTER_PLACEHOLDER} value={filters.member_id} onChange={(e)=>setFilters(prev=>({ ...prev, member_id:e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">From</label>
            <input type="date" className="input-field w-full" value={filters.from} onChange={(e)=>setFilters(prev=>({ ...prev, from:e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">To</label>
            <input type="date" className="input-field w-full" value={filters.to} onChange={(e)=>setFilters(prev=>({ ...prev, to:e.target.value }))} />
          </div>
          <div className="flex items-end"><button className="btn-primary" onClick={load}>Reload</button></div>
        </div>
      </div>
      <div className="card">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-medium text-gray-900">Daily Member Summary</h3>
          <div className="flex items-center gap-2">
            <button
              className="btn-secondary"
              onClick={async()=>{
                try {
                  setProcessing(true)
                  const body = { from: filters.from || undefined, to: filters.to || undefined }
                  await authFetch(getLocalApiUrl('/api/gaming/sessions/stats/rollup'), {
                    method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(body)
                  })
                  await load()
                } catch {} finally { setProcessing(false) }
              }}
            >Build Summary</button>
            <div className="text-sm text-gray-500">{processing ? 'Processing…' : (loading ? 'Loading…' : '')}</div>
          </div>
        </div>
        {error && <div className="text-sm text-red-600 mb-2">{error}</div>}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Player ID</th>
                <th className="px-3 py-2 text-left">Sessions</th>
                <th className="px-3 py-2 text-left">Avg Bet</th>
                <th className="px-3 py-2 text-left">Avg Turnover</th>
                <th className="px-3 py-2 text-left">Avg Wins</th>
                <th className="px-3 py-2 text-left">Avg Seconds</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {items.map((r, idx)=> (
                <tr key={`${r.MemberID}-${r.day_date}-${idx}`}>
                  <td className="px-3 py-2">{r.day_date?.slice(0,10) || '—'}</td>
                  <td className="px-3 py-2">{r.MemberID}</td>
                  <td className="px-3 py-2">{r.session_count}</td>
                  <td className="px-3 py-2">{Number(r.avg_bet ?? 0).toFixed(2)}</td>
                  <td className="px-3 py-2">{Number(r.avg_turnover ?? 0).toFixed(2)}</td>
                  <td className="px-3 py-2">{Number(r.avg_wins ?? 0).toFixed(2)}</td>
                  <td className="px-3 py-2">{Number(r.avg_seconds ?? 0).toFixed(0)}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td className="px-3 py-4 text-gray-500" colSpan={7}>No summary data</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// Violation Details Popup Component
function ViolationDetailsPopup({ violation, onClose, onAddToWatchlist, onCreateCase }) {
  if (!violation) return null

  const formatCurrency = (amount) => {
    if (amount == null || amount === '') return 'N/A'
    return formatMeterCents(amount)
  }

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A'
    return formatSydneyDateTime(dateStr)
  }

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL': return 'text-red-800 bg-red-100 border-red-200'
      case 'HIGH': return 'text-orange-800 bg-orange-100 border-orange-200'
      case 'MEDIUM': return 'text-yellow-800 bg-yellow-100 border-yellow-200'
      case 'LOW': return 'text-green-800 bg-green-100 border-green-200'
      default: return 'text-gray-800 bg-gray-100 border-gray-200'
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <ShieldExclamationIcon className="w-6 h-6 text-red-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Rule Violation Detected</h2>
              <p className="text-sm text-gray-600">Review details before taking action</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Rule Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Rule Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Rule Name</label>
                <p className="text-sm text-gray-900 mt-1">{violation.rule_name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Category</label>
                <p className="text-sm text-gray-900 mt-1">{violation.rule_category}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Severity</label>
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border mt-1 ${getSeverityColor(violation.severity)}`}>
                  {violation.severity}
                </span>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Confidence Score</label>
                <p className="text-sm text-gray-900 mt-1">{violation.confidence_score || 'N/A'}%</p>
              </div>
            </div>
          </div>

          {/* Member & Location Information */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
              <MapPinIcon className="w-5 h-5 mr-2 text-blue-600" />
              Member & Location Details
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Member #</label>
                <p className="text-sm text-gray-900 mt-1 font-mono">{violation.member_id || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">EGM House No</label>
                <p className="text-sm text-gray-900 mt-1 font-mono">{violation.egm_house_no || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Venue</label>
                <p className="text-sm text-gray-900 mt-1">{violation.venue || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Violation Summary */}
          <div className="bg-yellow-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
              <ExclamationTriangleIcon className="w-5 h-5 mr-2 text-yellow-600" />
              Violation Summary
            </h3>
            <p className="text-sm text-gray-800">{violation.violation_summary}</p>
            {violation.evidence_count > 0 && (
              <div className="mt-3">
                <span className="text-sm font-medium text-gray-700">Evidence Items: </span>
                <span className="text-sm text-gray-900">{violation.evidence_count} related records</span>
              </div>
            )}
          </div>

          {/* Detection Details */}
          <div className="bg-red-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
              <CalendarIcon className="w-5 h-5 mr-2 text-red-600" />
              Detection Details
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Detected At</label>
                <p className="text-sm text-gray-900 mt-1">{formatDateTime(violation.detected_at)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Session ID</label>
                <p className="text-sm text-gray-900 mt-1 font-mono">{violation.session_id || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Transaction/Financial Details */}
          {(violation.transaction_details || violation.cash_in || violation.turnover || violation.bill_details) && (
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                <CurrencyDollarIcon className="w-5 h-5 mr-2 text-green-600" />
                Financial Details
              </h3>
              <div className="grid grid-cols-3 gap-4 mb-4">
                {(violation.cash_in || violation.transaction_details?.cash_in) && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Cash In</label>
                    <p className="text-sm text-gray-900 mt-1 font-semibold">
                      {formatCurrency(violation.cash_in || violation.transaction_details?.cash_in)}
                    </p>
                  </div>
                )}
                {(violation.turnover || violation.transaction_details?.turnover) && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Turnover</label>
                    <p className="text-sm text-gray-900 mt-1 font-semibold">
                      {formatCurrency(violation.turnover || violation.transaction_details?.turnover)}
                    </p>
                  </div>
                )}
                {(violation.cash_out || violation.transaction_details?.cash_out) && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Cash Out</label>
                    <p className="text-sm text-gray-900 mt-1 font-semibold">
                      {formatCurrency(violation.cash_out || violation.transaction_details?.cash_out)}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Bill Denominations */}
              {(violation.bill_details || violation.transaction_details?.bills) && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Bill Denominations</label>
                  {violation.bill_details ? (
                    <p className="text-sm text-gray-900 bg-white p-2 rounded border">{violation.bill_details}</p>
                  ) : violation.transaction_details?.bills && (
                    <div className="grid grid-cols-5 gap-2">
                      {Object.entries(violation.transaction_details.bills).map(([denomination, count]) => 
                        count > 0 && (
                          <div key={denomination} className="bg-white p-2 rounded border text-center">
                            <div className="text-xs text-gray-600">{denomination.replace('bill', '$')}</div>
                            <div className="text-sm font-semibold">{count}</div>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Session Details */}
              {violation.session_details && (
                <div className="mt-4 pt-4 border-t border-green-200">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Session Information</label>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs text-gray-600">Start Time</label>
                      <p className="text-sm text-gray-900">{formatDateTime(violation.session_details.start_time)}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">End Time</label>
                      <p className="text-sm text-gray-900">{formatDateTime(violation.session_details.end_time)}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Duration</label>
                      <p className="text-sm text-gray-900">{Math.round(violation.session_details.duration_minutes)} minutes</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Recommended Actions */}
          <div className="bg-purple-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Recommended Actions</h3>
            <div className="space-y-2">
              {violation.severity === 'CRITICAL' && (
                <div className="flex items-center text-sm text-purple-800">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mr-2"></div>
                  Immediate investigation required - potential money laundering activity
                </div>
              )}
              {violation.severity === 'HIGH' && (
                <div className="flex items-center text-sm text-purple-800">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mr-2"></div>
                  Add to watchlist for enhanced monitoring
                </div>
              )}
              <div className="flex items-center text-sm text-purple-800">
                <div className="w-2 h-2 bg-purple-600 rounded-full mr-2"></div>
                Create case for detailed investigation
              </div>
              <div className="flex items-center text-sm text-purple-800">
                <div className="w-2 h-2 bg-purple-600 rounded-full mr-2"></div>
                Review member's recent activity patterns
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <ExclamationTriangleIcon className="w-4 h-4" />
            <span>This violation requires your attention</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
            <button
              onClick={() => onCreateCase(violation)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors"
            >
              Create Case
            </button>
            <button
              onClick={() => onAddToWatchlist(violation)}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 transition-colors flex items-center space-x-2"
            >
              <UserPlusIcon className="w-4 h-4" />
              <span>Add to Watchlist</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function RulesTestingTab() {
  const [rules, setRules] = React.useState([])
  const [selectedRules, setSelectedRules] = React.useState([])
  const [testResults, setTestResults] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [rulesLoading, setRulesLoading] = React.useState(true)
  const [selectedViolation, setSelectedViolation] = React.useState(null)
  const [showViolationPopup, setShowViolationPopup] = React.useState(false)
  const [filters, setFilters] = React.useState({ 
    member_id: '', 
    egm_pk: '', 
    from: '', 
    to: '',
    data_type: 'combined' // 'meter', 'sessions', 'combined'
  })
  const [testConfig, setTestConfig] = React.useState({
    batch_size: 100,
    create_audit_log: true,
    execution_source: 'gaming-data-ui'
  })

  // Load available rules
  React.useEffect(() => {
    const loadRules = async () => {
      try {
        setRulesLoading(true)
        const response = await authFetch(getLocalApiUrl('/api/rules?enabled=true'))
        const data = await response.json()
        if (data.success) {
          setRules(data.data || [])
        }
      } catch (error) {
        console.error('Failed to load rules:', error)
      } finally {
        setRulesLoading(false)
      }
    }
    loadRules()
  }, [])

  const runRulesTest = async () => {
    if (selectedRules.length === 0) {
      alert('Please select at least one rule to test')
      return
    }

    setLoading(true)
    setTestResults(null)

    try {
      // First, get the data based on filters and data type
      let dataEndpoint = ''
      let dataParams = new URLSearchParams()
      
      // Add common filters
      if (filters.member_id) dataParams.append('member_id', filters.member_id)
      if (filters.egm_pk) dataParams.append('egm_pk', filters.egm_pk)
      if (filters.from) dataParams.append('from', filters.from)
      if (filters.to) dataParams.append('to', filters.to)

      // Determine endpoint based on data type
      switch (filters.data_type) {
        case 'meter':
          dataEndpoint = '/api/gaming/egms/readings'
          break
        case 'sessions':
          dataEndpoint = '/api/gaming/sessions'
          break
        case 'combined':
        default:
          dataEndpoint = '/api/gaming/combined'
          break
      }

      // Fetch the data
      const dataResponse = await authFetch(getLocalApiUrl(`${dataEndpoint}?${dataParams.toString()}`))
      const dataResult = await dataResponse.json()
      
      if (!dataResult.success) {
        throw new Error(dataResult.error || 'Failed to fetch data')
      }

      const testData = dataResult.data || []
      
      if (testData.length === 0) {
        setTestResults({
          success: true,
          message: 'No data found matching the filters',
          batch_id: null,
          summary: { total_records: 0, processed: 0, triggered: 0, violations: [] }
        })
        return
      }

      // Run rules testing via batch processing
      const batchResponse = await authFetch(getLocalApiUrl('/api/rules/process-batch'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dataType: filters.data_type,
          records: testData.slice(0, testConfig.batch_size), // Limit batch size
          markProcessed: false, // Don't mark as processed for testing
          processingSource: testConfig.execution_source,
          selectedRules: selectedRules, // Pass selected rules
          createAuditLog: testConfig.create_audit_log,
          testMode: true
        })
      })

      const batchResult = await batchResponse.json()
      
      if (!batchResult.success) {
        throw new Error(batchResult.error || 'Rules testing failed')
      }

      // Process results for display
      const processedResults = {
        ...batchResult,
        summary: {
          total_records: batchResult.processed || 0,
          processed: batchResult.processed || 0,
          triggered: batchResult.triggered || 0,
          violations: batchResult.results?.filter(r => r.triggered_rules > 0) || [],
          rules_tested: selectedRules.length,
          data_type: filters.data_type,
          filters_applied: Object.entries(filters).filter(([k, v]) => v && k !== 'data_type').length
        }
      }

      setTestResults(processedResults)

    } catch (error) {
      console.error('Rules testing error:', error)
      setTestResults({
        success: false,
        error: error.message,
        summary: { total_records: 0, processed: 0, triggered: 0, violations: [] }
      })
    } finally {
      setLoading(false)
    }
  }

  const toggleRuleSelection = (ruleId) => {
    setSelectedRules(prev => 
      prev.includes(ruleId) 
        ? prev.filter(id => id !== ruleId)
        : [...prev, ruleId]
    )
  }

  const selectAllRules = () => {
    setSelectedRules(rules.map(r => r.id))
  }

  const clearRuleSelection = () => {
    setSelectedRules([])
  }

  // Handle viewing violation details
  const handleViewViolationDetails = (violation) => {
    setSelectedViolation(violation)
    setShowViolationPopup(true)
  }

  // Handle adding member to watchlist
  const handleAddToWatchlist = async (violation) => {
    try {
      const response = await authFetch(getLocalApiUrl('/api/watchlist/member'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          member_id: violation.member_id,
          reason: 'Rule Violation',
          rule_violation_id: violation.violation_id,
          severity: violation.severity,
          notes: `Added due to rule violation: ${violation.rule_name}. ${violation.violation_summary}`,
          source: 'rules-testing',
          metadata: {
            rule_id: violation.rule_id,
            rule_name: violation.rule_name,
            rule_category: violation.rule_category,
            confidence_score: violation.confidence_score,
            detected_at: violation.detected_at,
            egm_house_no: violation.egm_house_no,
            venue: violation.venue
          }
        })
      })

      const result = await response.json()
      
      if (result.success) {
        alert(`Member ${violation.member_id} has been added to the watchlist successfully.`)
        setShowViolationPopup(false)
        setSelectedViolation(null)
      } else {
        throw new Error(result.error || 'Failed to add to watchlist')
      }
    } catch (error) {
      console.error('Error adding to watchlist:', error)
      alert(`Failed to add member to watchlist: ${error.message}`)
    }
  }

  // Handle creating a case
  const handleCreateCase = async (violation) => {
    try {
      const response = await authFetch(getLocalApiUrl('/api/cases'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: `Rule Violation: ${violation.rule_name}`,
          description: violation.violation_summary,
          type: violation.rule_category,
          severity: violation.severity,
          member_id: violation.member_id,
          source: 'rules-violation',
          metadata: {
            rule_violation_id: violation.violation_id,
            rule_id: violation.rule_id,
            rule_name: violation.rule_name,
            rule_category: violation.rule_category,
            confidence_score: violation.confidence_score,
            detected_at: violation.detected_at,
            egm_house_no: violation.egm_house_no,
            venue: violation.venue,
            session_id: violation.session_id,
            evidence_count: violation.evidence_count
          },
          priority: violation.severity === 'CRITICAL' ? 'urgent' : 
                   violation.severity === 'HIGH' ? 'high' : 'normal',
          assigned_team: violation.rule_category === 'AML' ? 'AML_TEAM' : 'SECURITY_TEAM'
        })
      })

      const result = await response.json()
      
      if (result.success) {
        alert(`Case ${result.data.case_id} has been created successfully.`)
        setShowViolationPopup(false)
        setSelectedViolation(null)
        
        // Optionally redirect to case details
        // window.location.href = `/cases/${result.data.case_id}`
      } else {
        throw new Error(result.error || 'Failed to create case')
      }
    } catch (error) {
      console.error('Error creating case:', error)
      alert(`Failed to create case: ${error.message}`)
    }
  }

  // Close violation popup
  const handleCloseViolationPopup = () => {
    setShowViolationPopup(false)
    setSelectedViolation(null)
  }

  return (
    <div className="space-y-6">
      {/* Test Configuration */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Rules Testing Configuration</h3>
        
        {/* Data Filters */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Data Selection</h4>
          <div className="grid grid-cols-6 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Data Type</label>
              <select 
                className="input-field w-full" 
                value={filters.data_type} 
                onChange={(e) => setFilters(prev => ({ ...prev, data_type: e.target.value }))}
              >
                <option value="combined">Combined Data</option>
                <option value="sessions">Gaming Sessions</option>
                <option value="meter">Meter Readings</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{GAMING_MEMBER_FILTER_LABEL}</label>
              <input
                className="input-field w-full"
                placeholder={GAMING_MEMBER_FILTER_PLACEHOLDER}
                value={filters.member_id} 
                onChange={(e) => setFilters(prev => ({ ...prev, member_id: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">House / EGM</label>
              <input 
                className="input-field w-full" 
                value={filters.egm_pk} 
                onChange={(e) => setFilters(prev => ({ ...prev, egm_pk: e.target.value }))} 
                placeholder="010038 or 38"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">From</label>
              <input 
                type="datetime-local" 
                className="input-field w-full" 
                value={filters.from} 
                onChange={(e) => setFilters(prev => ({ ...prev, from: e.target.value }))} 
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">To</label>
              <input 
                type="datetime-local" 
                className="input-field w-full" 
                value={filters.to} 
                onChange={(e) => setFilters(prev => ({ ...prev, to: e.target.value }))} 
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Batch Size</label>
              <input 
                type="number" 
                min="1" 
                max="1000" 
                className="input-field w-full" 
                value={testConfig.batch_size} 
                onChange={(e) => setTestConfig(prev => ({ ...prev, batch_size: parseInt(e.target.value) || 100 }))} 
              />
            </div>
          </div>
        </div>

        {/* Rule Selection */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-700">Rules to Test ({selectedRules.length} selected)</h4>
            <div className="flex gap-2">
              <button 
                className="btn-secondary text-xs" 
                onClick={selectAllRules}
                disabled={rulesLoading}
              >
                Select All
              </button>
              <button 
                className="btn-secondary text-xs" 
                onClick={clearRuleSelection}
              >
                Clear All
              </button>
            </div>
          </div>
          
          {rulesLoading ? (
            <div className="text-sm text-gray-500">Loading rules...</div>
          ) : (
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded p-3">
              {rules.map(rule => (
                <label key={rule.id} className="flex items-center space-x-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedRules.includes(rule.id)}
                    onChange={() => toggleRuleSelection(rule.id)}
                    className="rounded border-gray-300"
                  />
                  <span className="flex-1">
                    <span className="font-medium">{rule.name}</span>
                    <span className="text-gray-500 ml-2">({rule.type} - {rule.category})</span>
                  </span>
                </label>
              ))}
              {rules.length === 0 && (
                <div className="col-span-2 text-sm text-gray-500 text-center py-4">
                  No rules available
                </div>
              )}
            </div>
          )}
        </div>

        {/* Run Test Button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                checked={testConfig.create_audit_log}
                onChange={(e) => setTestConfig(prev => ({ ...prev, create_audit_log: e.target.checked }))}
                className="rounded border-gray-300"
              />
              <span>Create audit log</span>
            </label>
          </div>
          <button
            className="btn-primary flex items-center space-x-2"
            onClick={runRulesTest}
            disabled={loading || selectedRules.length === 0}
          >
            {loading ? (
              <>
                <ClockIcon className="w-4 h-4 animate-spin" />
                <span>Testing Rules...</span>
              </>
            ) : (
              <>
                <PlayIcon className="w-4 h-4" />
                <span>Run Rules Test</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Test Results */}
      {testResults && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Test Results</h3>
            {testResults.batch_id && (
              <span className="text-xs text-gray-500 font-mono">Batch: {testResults.batch_id}</span>
            )}
          </div>

          {/* Results Summary */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center">
                <DocumentTextIcon className="w-5 h-5 text-blue-600 mr-2" />
                <div>
                  <div className="text-2xl font-bold text-blue-600">{testResults.summary.total_records}</div>
                  <div className="text-sm text-gray-600">Records Processed</div>
                </div>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center">
                <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
                <div>
                  <div className="text-2xl font-bold text-green-600">{testResults.summary.processed}</div>
                  <div className="text-sm text-gray-600">Successfully Processed</div>
                </div>
              </div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mr-2" />
                <div>
                  <div className="text-2xl font-bold text-yellow-600">{testResults.summary.triggered}</div>
                  <div className="text-sm text-gray-600">Rules Triggered</div>
                </div>
              </div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center">
                <ChartBarIcon className="w-5 h-5 text-red-600 mr-2" />
                <div>
                  <div className="text-2xl font-bold text-red-600">{testResults.summary.violations.length}</div>
                  <div className="text-sm text-gray-600">Violations Found</div>
                </div>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {!testResults.success && testResults.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mr-2" />
                <div>
                  <div className="font-medium text-red-800">Test Failed</div>
                  <div className="text-sm text-red-600">{testResults.error}</div>
                </div>
              </div>
            </div>
          )}

          {/* Violations Details */}
          {testResults.summary.violations.length > 0 && (
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">Violations Detected</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Record ID</th>
                      <th className="px-3 py-2 text-left">Rules Triggered</th>
                      <th className="px-3 py-2 text-left">Severity</th>
                      <th className="px-3 py-2 text-left">Details</th>
                      <th className="px-3 py-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {testResults.summary.violations.map((violation, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2 font-mono">{violation.record_id}</td>
                        <td className="px-3 py-2">{violation.triggered_rules}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                            violation.severity === 'CRITICAL' ? 'bg-red-100 text-red-800' :
                            violation.severity === 'HIGH' ? 'bg-orange-100 text-orange-800' :
                            violation.severity === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {violation.severity || 'MEDIUM'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {violation.rules?.map(rule => rule.name).join(', ') || 'N/A'}
                        </td>
                        <td className="px-3 py-2">
                          <button 
                            className="text-blue-600 hover:text-blue-800 text-xs"
                            onClick={() => handleViewViolationDetails(violation)}
                          >
                            <EyeIcon className="w-4 h-4 inline mr-1" />
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* No Violations Message */}
          {testResults.success && testResults.summary.violations.length === 0 && testResults.summary.total_records > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
                <div>
                  <div className="font-medium text-green-800">All Clear!</div>
                  <div className="text-sm text-green-600">
                    No rule violations detected in {testResults.summary.total_records} records tested.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Violation Details Popup */}
      {showViolationPopup && selectedViolation && (
        <ViolationDetailsPopup
          violation={selectedViolation}
          onClose={handleCloseViolationPopup}
          onAddToWatchlist={handleAddToWatchlist}
          onCreateCase={handleCreateCase}
        />
      )}
    </div>
  )
}

// ─── EGM Registry Tab ────────────────────────────────────────────────────────
function EgmRegistryTab() {
  const [search, setSearch] = React.useState('')
  const [activeFilter, setActiveFilter] = React.useState('')
  const [rows, setRows] = React.useState([])
  const [total, setTotal] = React.useState(0)
  const [page, setPage] = React.useState(1)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)
  const LIMIT = 100

  const load = React.useCallback(async (pg = 1) => {
    setLoading(true); setError(null)
    try {
      const params = new URLSearchParams({ page: pg, limit: LIMIT })
      if (search.trim()) params.set('search', search.trim())
      if (activeFilter !== '') params.set('active', activeFilter)
      const r = await authFetch(getLocalApiUrl(`/api/gaming/egm-registry?${params}`))
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed')
      setRows(j.data || [])
      setTotal(j.total || 0)
      setPage(pg)
    } catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }, [search, activeFilter])

  React.useEffect(() => { load(1) }, [load])

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Search (house #, game, serial)</label>
          <input
            className="input-field w-52"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load(1)}
            placeholder="e.g. 010001 or ARISTOCRAT"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Status</label>
          <select className="input-field" value={activeFilter} onChange={e => setActiveFilter(e.target.value)}>
            <option value="">All</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
        <button className="btn-primary" onClick={() => load(1)}>Search</button>
      </div>

      {error && <div className="rounded bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>}

      <p className="text-xs text-gray-500">
        Raw <code className="text-xs">egm_registry</code> — machine master populated from DimMachine daily imports.
        {total > 0 && ` Showing ${rows.length} of ${total} records.`}
      </p>

      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['House #','Type','EGM ID','Serial','Vendor PK','Game Name','Mfr','Denom','Zone','Area','Status','Active','Site','Install','Updated'].map(h => (
                <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={15} className="px-3 py-6 text-center text-gray-400">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={15} className="px-3 py-6 text-center text-gray-400">No records — check filters or import EGM data via Daily Data → DimPatron / Daily Data → EGM</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className={`hover:bg-gray-50 ${r.active === false ? 'opacity-50' : ''}`}>
                <td className="px-3 py-2 font-mono">{r.house_number || '—'}</td>
                <td className="px-3 py-2">
                  {String(r.machine_class || '').toUpperCase() === 'MTGM' ? (
                    <span className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 text-xs">MTGM</span>
                  ) : String(r.machine_class || '').toUpperCase() === 'EGM' ? (
                    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-xs">EGM</span>
                  ) : '—'}
                </td>
                <td className="px-3 py-2 font-mono text-gray-500">{r.egm_id}</td>
                <td className="px-3 py-2 font-mono text-gray-500">{r.serial_number || '—'}</td>
                <td className="px-3 py-2 font-mono text-gray-500">{r.vendor_machine_pk || '—'}</td>
                <td className="px-3 py-2 max-w-[180px] truncate" title={r.game_name}>{r.game_name || '—'}</td>
                <td className="px-3 py-2">{r.manufacturer || '—'}</td>
                <td className="px-3 py-2">{r.denom != null ? `$${Number(r.denom).toFixed(2)}` : '—'}</td>
                <td className="px-3 py-2">{r.zone || '—'}</td>
                <td className="px-3 py-2">{r.area || '—'}</td>
                <td className="px-3 py-2">{r.status || '—'}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${r.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {r.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-2 max-w-[120px] truncate" title={r.site_name}>{r.site_name || '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.install_date ? r.install_date.slice(0, 10) : '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap text-gray-400">{r.updated_at ? r.updated_at.slice(0, 10) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > LIMIT && (
        <div className="flex items-center gap-2 text-sm">
          <button className="btn-secondary" disabled={page <= 1} onClick={() => load(page - 1)}>Previous</button>
          <span className="text-gray-500">Page {page} of {Math.ceil(total / LIMIT)}</span>
          <button className="btn-secondary" disabled={page >= Math.ceil(total / LIMIT)} onClick={() => load(page + 1)}>Next</button>
        </div>
      )}
    </div>
  )
}

// ─── Slot Analysis Tab ────────────────────────────────────────────────────────
function SlotAnalysisTab({ initialFilters }) {
  const [filters, setFilters] = React.useState({
    egm_id: initialFilters?.egm_pk || initialFilters?.egm_id || '',
    from: initialFilters?.from || defaultMeterFromDate(),
    to: initialFilters?.to || defaultMeterToDate(),
    jackpotFilter: 'jackpots',
  })
  const [rows, setRows] = React.useState([])
  const [total, setTotal] = React.useState(0)
  const [page, setPage] = React.useState(1)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)
  const LIMIT = 100

  const load = React.useCallback(async (pg = 1) => {
    setLoading(true); setError(null)
    try {
      const params = new URLSearchParams({ page: pg, limit: LIMIT })
      if (filters.egm_id) params.set('egm_pk', filters.egm_id)
      if (filters.from) params.set('from', toApiDateTimeParam(filters.from))
      if (filters.to) params.set('to', toApiDateTimeParam(filters.to))
      params.set('jackpot_only', filters.jackpotFilter === 'jackpots' ? 'true' : 'false')
      const r = await authFetch(getLocalApiUrl(`/api/gaming/slot-analysis?${params}`))
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed')
      setRows(j.data || [])
      setTotal(j.total || 0)
      setPage(pg)
    } catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }, [filters])

  React.useEffect(() => { load(1) }, [load])

  const fmt = (v) => v != null ? Number(v).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'
  const cardPct = (row) => {
    const t = Number(row.turnover); const rt = Number(row.rated_turnover)
    if (!t) return '—'
    return `${(rt / t * 100).toFixed(1)}%`
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <label className="block text-xs text-gray-600 mb-1">EGM PK / House #</label>
          <input
            className="input-field w-32"
            value={filters.egm_id}
            onChange={e => setFilters(p => ({ ...p, egm_id: e.target.value }))}
            placeholder="e.g. 010001"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">From</label>
          <input type="date" className="input-field" value={filters.from} onChange={e => setFilters(p => ({ ...p, from: e.target.value }))} />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">To</label>
          <input type="date" className="input-field" value={filters.to} onChange={e => setFilters(p => ({ ...p, to: e.target.value }))} />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Show</label>
          <select
            className="input-field"
            value={filters.jackpotFilter}
            onChange={e => setFilters(p => ({ ...p, jackpotFilter: e.target.value }))}
            title="Jackpots only = EGM-days with jackpot win or progressive voucher &gt; 0"
          >
            <option value="jackpots">Jackpots only</option>
            <option value="all">All EGMs</option>
          </select>
        </div>
        <button className="btn-primary" onClick={() => load(1)}>Search</button>
      </div>

      {error && <div className="rounded bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>}

      <p className="text-xs text-gray-500">
        Raw <code className="text-xs">fact_slot_analysis_daily</code> — daily per-EGM aggregates from FactSlotAnalysisDaily imports.
        <strong className="ml-1">Card %</strong> = rated_turnover ÷ turnover × 100.
        {filters.jackpotFilter === 'jackpots' && (
          <span className="ml-1">Filtered to EGM-days with jackpot win or progressive voucher &gt; 0.</span>
        )}
        {total > 0 && ` Showing ${rows.length} of ${total} records.`}
      </p>

      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['Date','EGM ID','EGM PK','Turnover','Rated TO','Card %','Jackpot Win','Sessions','Games','Net Credit Win','Machine Retained','Cash In','Cash Out','Valid'].map(h => (
                <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={14} className="px-3 py-6 text-center text-gray-400">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={14} className="px-3 py-6 text-center text-gray-400">
                {filters.jackpotFilter === 'jackpots'
                  ? 'No jackpot/progressive records in this date range — switch Show to All EGMs or widen dates'
                  : 'No records — import FactSlotAnalysisDaily files via Daily Data → Slot Analysis'}
              </td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className={`hover:bg-gray-50 ${r.valid_flag === 'No' ? 'bg-yellow-50' : ''}`}>
                <td className="px-3 py-2 font-mono whitespace-nowrap">{r.gaming_date ? String(r.gaming_date).slice(0, 10) : '—'}</td>
                <td className="px-3 py-2 font-mono">{r.egm_id}</td>
                <td className="px-3 py-2 font-mono text-gray-500">{r.egm_pk || '—'}</td>
                <td className="px-3 py-2 text-right">{fmt(r.turnover)}</td>
                <td className="px-3 py-2 text-right">{fmt(r.rated_turnover)}</td>
                <td className="px-3 py-2 text-right font-medium">
                  <span className={Number(r.rated_turnover) / Number(r.turnover || 1) < 0.1 ? 'text-amber-600' : 'text-gray-900'}>
                    {cardPct(r)}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <span className={Number(r.jackpot_win) > 0 ? 'text-blue-600 font-medium' : 'text-gray-500'}>
                    {fmt(r.jackpot_win)}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">{r.session_count ?? '—'}</td>
                <td className="px-3 py-2 text-right">{r.games_played ?? '—'}</td>
                <td className="px-3 py-2 text-right">{fmt(r.net_credit_win)}</td>
                <td className="px-3 py-2 text-right">{fmt(r.machine_retained)}</td>
                <td className="px-3 py-2 text-right">{fmt(r.cash_in)}</td>
                <td className="px-3 py-2 text-right">{fmt(r.cash_out)}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${r.valid_flag === 'Yes' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {r.valid_flag || '—'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > LIMIT && (
        <div className="flex items-center gap-2 text-sm">
          <button className="btn-secondary" disabled={page <= 1} onClick={() => load(page - 1)}>Previous</button>
          <span className="text-gray-500">Page {page} of {Math.ceil(total / LIMIT)}</span>
          <button className="btn-secondary" disabled={page >= Math.ceil(total / LIMIT)} onClick={() => load(page + 1)}>Next</button>
        </div>
      )}
    </div>
  )
}

export default function GamingData() {
  const [searchParams] = useSearchParams()
  const ctx = React.useMemo(() => readGamingDataSearchParams(searchParams), [searchParams])
  const [tab, setTab] = React.useState(() => ctx.tab || 'meter')
  React.useEffect(() => {
    if (ctx.tab) setTab(ctx.tab)
  }, [ctx.tab])
  const initialFilters = React.useMemo(
    () => ({
      egm_pk: ctx.egm_pk,
      egmpks: ctx.egmpks,
      member_id: ctx.member_id,
      from: ctx.from,
      to: ctx.to,
      trading_day: ctx.trading_day,
      view: ctx.view,
      hours: ctx.hours,
      machine_class: ctx.machine_class,
    }),
    [
      ctx.egm_pk,
      ctx.egmpks,
      ctx.member_id,
      ctx.from,
      ctx.to,
      ctx.trading_day,
      ctx.view,
      ctx.hours,
      ctx.machine_class,
    ]
  )

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      {ctx.alert_id && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-700">
          Opened from monitoring alert <span className="font-mono font-semibold">#{ctx.alert_id}</span>
          {' · '}
          Filters below are pre-filled from that alert&apos;s time window (±30 minutes when linked from alert detail).
        </div>
      )}
      <div className="card">
        <div className="flex gap-3 border-b">
          <button className={`px-3 py-2 ${tab==='import'?'border-b-2 border-psg-teal text-psg-teal':'text-gray-600'}`} onClick={()=>setTab('import')}>
            <ArrowUpTrayIcon className="w-4 h-4 inline mr-1" />
            Import
          </button>
          <button className={`px-3 py-2 ${tab==='meter'?'border-b-2 border-psg-teal text-psg-teal':'text-gray-600'}`} onClick={()=>setTab('meter')}>Meter Reading</button>
          <button className={`px-3 py-2 ${tab==='sessions'?'border-b-2 border-psg-teal text-psg-teal':'text-gray-600'}`} onClick={()=>setTab('sessions')}>Sessions</button>
          <button className={`px-3 py-2 ${tab==='combined'?'border-b-2 border-psg-teal text-psg-teal':'text-gray-600'}`} onClick={()=>setTab('combined')}>Combined</button>
          <button className={`px-3 py-2 ${tab==='summary'?'border-b-2 border-psg-teal text-psg-teal':'text-gray-600'}`} onClick={()=>setTab('summary')}>Summary</button>
          <button className={`px-3 py-2 ${tab==='egm-registry'?'border-b-2 border-psg-teal text-psg-teal':'text-gray-600'}`} onClick={()=>setTab('egm-registry')}>
            <CpuChipIcon className="w-4 h-4 inline mr-1" />
            EGM Registry
          </button>
          <button className={`px-3 py-2 ${tab==='slot-analysis'?'border-b-2 border-psg-teal text-psg-teal':'text-gray-600'}`} onClick={()=>setTab('slot-analysis')}>
            <TableCellsIcon className="w-4 h-4 inline mr-1" />
            Slot Analysis
          </button>
          <button className={`px-3 py-2 ${tab==='rules-test'?'border-b-2 border-psg-teal text-psg-teal':'text-gray-600'}`} onClick={()=>setTab('rules-test')}>
            <PlayIcon className="w-4 h-4 inline mr-1" />
            Rules Testing
          </button>
        </div>
        <p className="text-xs text-gray-500 px-1 pt-2 pb-3 border-b border-gray-100">
          {GAMING_SOURCE_TABS_NOTE} {GAMING_COMBINED_NOTE}
        </p>
        <div className="pt-4">
          {tab==='import' ? <ImportWizardTab /> :
           tab==='meter' ? <MeterTab initialFilters={initialFilters} /> :
           tab==='sessions' ? <SessionsTab initialFilters={initialFilters} /> :
           tab==='combined' ? <CombinedTab initialFilters={initialFilters} /> :
           tab==='summary' ? <SummaryTab initialFilters={initialFilters} /> :
           tab==='egm-registry' ? <EgmRegistryTab /> :
           tab==='slot-analysis' ? <SlotAnalysisTab initialFilters={initialFilters} /> :
           tab==='rules-test' ? <RulesTestingTab /> : <MeterTab initialFilters={initialFilters} />}
        </div>
      </div>
    </div>
  )
}


